import React, { useState, useEffect, useRef } from 'react';
import { API_PATHS } from '../../../utils/constants';
import QuickAssignDialog from './QuickAssignDialog';
import DocumentViewer from '../../../Components/Common/DocumentViewer';
import StatusChangeDialog from '../../../Components/Ticket/StatusChangeDialog';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Assignment as AssignmentIcon,
  Chat as ChatIcon,
  History as HistoryIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Refresh as RefreshIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  FileUpload as FileUploadIcon,
  CloudUpload as CloudUploadIcon,
  Save as SaveIcon,
  Image as ImageIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  TableChart as TableChartIcon
} from '@mui/icons-material';
import TabsNav from '../../../Components/Common/TabsNav';
import { useNavigate, useLocation, useParams, useBeforeUnload } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { showLoader, dismissLoader } from '../../../LayoutContainers/AppLayout/redux/AppSlice';
import AppService from '../../../Configurations/Appservice';
import AssignmentPanel from './AssignmentPanel';
import AssignmentHistory from './AssignmentHistory';
import { FORMAT_DATE } from '../../../Configurations/JsUtil';
import apiService from '../../../services/api';
import { API_ENDPOINTS, API_BASE_V1 } from '../../../Api/endpoints';
import {
  toProcessingDocumentType,
  isExtractionSupported,
  buildSharedMetadataFromExtraction,
  buildEmployeeUpdatePayloadFromPassport,
  buildEmployeeUpdatePayloadFromOhc,
  buildEmployeeUpdatePayloadFromDrivingLicense,
} from '../../../utils/employeeDocumentUtils';

// Safe extractor for the last path segment of a string
const lastSegment = (value, fallback = 'Document') => {
  if (!value || typeof value !== 'string') return fallback;
  const idx = value.lastIndexOf('/');
  if (idx === -1) return value || fallback;
  const seg = value.substring(idx + 1);
  return seg || fallback;
};

// Status chip component with compact design
const StatusChip = ({ status }) => {
  let color = 'default';
  let bgColor = 'default';
  let label = 'Unknown';
  const statusText = status || 'Unknown';
  
  // Debug logging to see what status is being processed
  console.log('StatusChip rendering with status:', statusText);
  
  // Determine color and label based on status
  switch (status?.toUpperCase()) {
    case 'OPEN':
      color = '#1565C0';
      bgColor = '#E3F2FD';
      label = 'Open';
      break;
    case 'TAKEN':
      color = '#7B1FA2';
      bgColor = '#F3E5F5';
      label = 'Taken';
      break;
    case 'ASSIGNED':
      color = '#00796B';
      bgColor = '#E0F2F1';
      label = 'Assigned';
      break;
    case 'ASSIGNED_TO_ORGANIZATION':
      color = '#0277BD';
      bgColor = '#E1F5FE';
      label = 'Assigned to Organization';
      break;
    case 'IN_PROGRESS':
      color = '#F57F17';
      bgColor = '#FFF8E1';
      label = 'In Progress';
      break;
    case 'COMPLETED':
      color = '#2E7D32';
      bgColor = '#E8F5E9';
      label = 'Completed';
      break;
    case 'CLOSED':
      color = '#212121';
      bgColor = '#F5F5F5';
      label = 'Closed';
      break;
    case 'REJECTED':
      color = '#C62828';
      bgColor = '#FFEBEE';
      label = 'Rejected';
      break;
    case 'INCOMPLETE':
      color = '#EF6C00';
      bgColor = '#FFF3E0';
      label = 'Incomplete';
      break;
    case 'MODIFICATION_REQUIRED':
      color = '#6D4C41';
      bgColor = '#EFEBE9';
      label = 'Modification Required';
      break;
    default:
      console.warn(`Unknown status: ${status}`);
      color = '#757575';
      bgColor = '#F5F5F5';
      label = status || 'Unknown';
  }
  
  return (
    <Chip 
      label={label}
      size="small"
      sx={{
        height: '24px',
        backgroundColor: bgColor,
        color: color,
        '& .MuiChip-label': {
          px: 1.5,
          fontSize: '0.75rem',
          fontWeight: 500
        }
      }}
    />
  );
};

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ticket-tabpanel-${index}`}
      aria-labelledby={`ticket-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TicketDetailView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('basic-info');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [addActionDialogOpen, setAddActionDialogOpen] = useState(false);
  const [availableActions, setAvailableActions] = useState([]);
  const [selectedActionId, setSelectedActionId] = useState('');
  const [selectedAction, setSelectedAction] = useState(null);
  const [actionDueDate, setActionDueDate] = useState('');
  const [actionRemarks, setActionRemarks] = useState('');
  // Edit Action dialog state
  const [editActionDialogOpen, setEditActionDialogOpen] = useState(false);
  const [editActionData, setEditActionData] = useState({
    id: '',
    name: '',
    status: 'PENDING',
    dueDate: '',
    remarks: ''
  });
  const [supportingDocs, setSupportingDocs] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [additionalDocuments, setAdditionalDocuments] = useState([]);
  const [additionalDocFile, setAdditionalDocFile] = useState(null);
  const [additionalDocMeta, setAdditionalDocMeta] = useState({
    name: '',
    description: '',
    type: 'GENERAL'
  });
  // Document Type list (DB-backed)
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loadingDocTypes, setLoadingDocTypes] = useState(false);
  const [otherTypeName, setOtherTypeName] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [loadingAdditionalDocs, setLoadingAdditionalDocs] = useState(false);
  const [loadingSupportingDocs, setLoadingSupportingDocs] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState({
    url: '',
    fileName: '',
    uid: '',
    documentType: ''
  });
  const [canAssign, setCanAssign] = useState(false);
  const [isStatusChanged, setIsStatusChanged] = useState(true);
  const [showBackConfirmDialog, setShowBackConfirmDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const fileInputRef = useRef(null);
  const auth = useSelector((state) => state.auth);

  // === Employee document flow state ===
  const [employeeDocTypeCodes, setEmployeeDocTypeCodes] = useState([]);
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeDocUploading, setEmployeeDocUploading] = useState(false);
  // Passport update confirmation state
  const [passportUpdatePromptOpen, setPassportUpdatePromptOpen] = useState(false);
  const [passportExtractedNew, setPassportExtractedNew] = useState(null);
  const [passportUpdatePayload, setPassportUpdatePayload] = useState(null);
  const [passportEditMode, setPassportEditMode] = useState({});
  // OHC update confirmation state
  const [ohcUpdatePromptOpen, setOhcUpdatePromptOpen] = useState(false);
  const [ohcExtractedNew, setOhcExtractedNew] = useState(null);
  const [ohcUpdatePayload, setOhcUpdatePayload] = useState(null);
  const [ohcEditMode, setOhcEditMode] = useState({});
  // Driving License update confirmation state
  const [drivingLicenseUpdatePromptOpen, setDrivingLicenseUpdatePromptOpen] = useState(false);
  const [drivingLicenseExtractedNew, setDrivingLicenseExtractedNew] = useState(null);
  const [drivingLicenseUpdatePayload, setDrivingLicenseUpdatePayload] = useState(null);
  const [drivingLicenseEditMode, setDrivingLicenseEditMode] = useState({});
  const pendingBulkSaveRef = useRef(null);

  // Transaction number / completion date edit state
  const [transactionNumberDialogOpen, setTransactionNumberDialogOpen] = useState(false);
  const [editingTransactionNumber, setEditingTransactionNumber] = useState('');
  const [editingCompletionDate, setEditingCompletionDate] = useState('');
  const [updatingTransactionNumber, setUpdatingTransactionNumber] = useState(false);
  
  // Helper: read roles from JWT and check role membership
  const parseRolesFromToken = () => {
    try {
      const token = localStorage.getItem('jwt_token') || (localStorage.getItem('authInfo') ? JSON.parse(localStorage.getItem('authInfo')).token : null);
      if (!token) return [];
      const parts = token.split('.');
      if (parts.length !== 3) return [];
      const payload = JSON.parse(atob(parts[1]));
      const roles = payload && payload.roles ? payload.roles : [];
      return Array.isArray(roles)
        ? roles.map(r => (typeof r === 'string' ? r : (r && r.authority) || '')).filter(Boolean)
        : [];
    } catch (e) {
      console.warn('Failed to parse roles from token:', e?.message || e);
      return [];
    }
  };

  const roles = parseRolesFromToken();
  const hasRole = (r) => roles.includes(r);
  // Assignment tab is visible ONLY to ROOT_ADMIN or ROOT_EMPLOYEE
  const canSeeAssignment = hasRole('ROLE_ROOT_ADMIN') || hasRole('ROLE_ROOT_EMPLOYEE');

  // Helper: parse basic user identity from JWT
  const parseUserFromToken = () => {
    try {
      const token = localStorage.getItem('jwt_token') || (localStorage.getItem('authInfo') ? JSON.parse(localStorage.getItem('authInfo')).token : null);
      if (!token) return {};
      const parts = token.split('.');
      if (parts.length !== 3) return {};
      const payload = JSON.parse(atob(parts[1]));
      return {
        id: payload?.id || payload?.userId || payload?.uid || null,
        email: payload?.email || payload?.emailId || payload?.username || payload?.sub || null,
        name: payload?.name || payload?.fullName || null,
      };
    } catch (e) {
      console.warn('Failed to parse user from token:', e?.message || e);
      return {};
    }
  };
  const me = parseUserFromToken();

  // Compute assignment ownership
  const isAssignedToMe = (() => {
    try {
      if (!ticket) return false;

      // ROOT_ADMIN should always be able to assign without org-config check
      if (hasRole('ROLE_ROOT_ADMIN') || hasRole('ROLE_ROOT_EMPLOYEE')) {
        return true;
      }

      const meId = me?.id ? String(me.id) : null;
      const meEmail = me?.email ? String(me.email).toLowerCase() : null;
      const assignedUser = ticket.assignedUser || ticket.ticket?.assignedUser || null;
      const claimed = ticket.claimedByUser || ticket.claimedBy || null;

      const assignedId = assignedUser?.id != null ? String(assignedUser.id) : null;
      const assignedEmail = assignedUser?.email ? String(assignedUser.email).toLowerCase() : null;
      const claimedId = claimed?.id != null ? String(claimed.id) : null;
      const claimedEmail = claimed?.emailId ? String(claimed.emailId).toLowerCase() : (claimed?.email ? String(claimed.email).toLowerCase() : null);
      console.log({
        meId, meEmail, assignedId, assignedEmail, claimedId, claimedEmail
      });
      
      return (
        (!!meId && (meId === assignedId || meId === claimedId)) ||
        (!!meEmail && (meEmail === assignedEmail || meEmail === claimedEmail))
      );
    } catch {
      return false;
    }
  })();
  
  // If user cannot see assignment but somehow lands on the tab, move them away
  useEffect(() => {
    if (!canSeeAssignment && activeTab === 'assignment') {
      setActiveTab('basic-info');
    }
  }, [canSeeAssignment, activeTab]);
  
  // Get ticketId from URL path parameters, query parameters, or location state
  const params = useParams();
  const ticketId = params.id || location.state?.ticketId || new URLSearchParams(location.search).get('id');
  
  // Make copy of ticketId to handle navigation changes
  const currentTicketId = ticketId;
  
  // Get authentication token
  const getAuthToken = () => {
    return localStorage.getItem('jwt_token') || 
      (localStorage.getItem('authInfo') ? 
      JSON.parse(localStorage.getItem('authInfo')).token : null);
  };

  // Convert a YYYY-MM-DD (or ISO) string to UTC ISO (Z) format expected by backend
  const toUtcISOString = (dateStr) => {
    try {
      if (!dateStr) return null;
      // If it's date-only, build midnight UTC for that date
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [yyyy, mm, dd] = dateStr.split('-').map(Number);
        return new Date(Date.UTC(yyyy, (mm || 1) - 1, dd || 1, 0, 0, 0)).toISOString();
      }
      // Otherwise try to parse and convert to ISO
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) return d.toISOString();
      return null;
    } catch {
      return null;
    }
  };

  // Helper to derive serviceId from ticket object (covers multiple possible shapes)
  const getServiceIdFromTicket = (t) => {
    const tk = t || ticket;
    if (!tk) return null;
    return (
      tk.serviceId ||
      tk.serviceID ||
      tk.service_id ||
      tk.service?.id ||
      tk.ticket?.serviceId ||
      tk.ticket?.service?.id ||
      null
    );
  };

  // Fetch document types from backend
  const fetchDocumentTypes = async () => {
    try {
      setLoadingDocTypes(true);
      const token = getAuthToken();
      if (!token) return;
      // Fetch ALL active document types (BOTH + ADDITIONAL) by omitting scope filter
      const res = await fetch(`${API_PATHS.V1}/document-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return;
      const list = await res.json();
      // Sort by label for nicer UX
      const sorted = Array.isArray(list) ? list.sort((a,b)=> (a.label||'').localeCompare(b.label||'')) : [];
      setDocumentTypes(sorted);
      // If current type is still the default placeholder, set to 'other' if exists, otherwise first code
      if (!additionalDocMeta.type || additionalDocMeta.type === 'GENERAL') {
        const otherExists = sorted.find(dt => (dt.code||'').toLowerCase() === 'other');
        setAdditionalDocMeta(prev => ({...prev, type: otherExists ? 'other' : (sorted[0]?.code || 'GENERAL')}));
      }
    } catch (e) {
      console.warn('Failed to load document types:', e?.message || e);
    } finally {
      setLoadingDocTypes(false);
    }
  };

  useEffect(() => {
    fetchDocumentTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch employee document type codes for detecting employee doc types
  useEffect(() => {
    const fetchEmployeeDocTypeCodes = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await fetch(`${API_PATHS.V1}/document-types`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const list = await res.json();
        if (Array.isArray(list)) {
          setEmployeeDocTypeCodes(list.map(dt => dt.code));
        }
      } catch (e) {
        console.warn('Failed to load employee document type codes:', e?.message || e);
      }
    };
    fetchEmployeeDocTypeCodes();
  }, []);

  // Derived: employeeUid from ticket data
  const employeeUid = ticket?.employeeUid || null;

  // Fetch employee data when ticket has employeeUid (needed for extraction diff dialogs)
  useEffect(() => {
    if (!employeeUid) {
      setEmployeeData(null);
      return;
    }
    const fetchEmployee = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await fetch(`${API_PATHS.V1}/employees/${encodeURIComponent(employeeUid)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const resp = await res.json();
        setEmployeeData(resp?.data || resp || null);
      } catch (e) {
        console.warn('Failed to fetch employee data for extraction diffs:', e?.message || e);
      }
    };
    fetchEmployee();
  }, [employeeUid]);

  // Helper: check if a document type code is a known employee document type
  const isEmployeeDocType = (code) => {
    if (!code || employeeDocTypeCodes.length === 0) return false;
    const normalized = String(code).toLowerCase().replace(/[\s-]/g, '_');
    return employeeDocTypeCodes.some(c => String(c).toLowerCase().replace(/[\s-]/g, '_') === normalized);
  };

  useEffect(() => {
    if(!currentTicketId || !ticket) return;

    // ROOT_ADMIN should always be able to assign without org-config check
    if (hasRole('ROLE_ROOT_ADMIN') || hasRole('ROLE_ROOT_EMPLOYEE')) {
      setCanAssign(true);
      return;
    }
    
    if (ticket.assignment?.assignedTo === "THAKLIS"){
      setCanAssign(false);
      return;
    }

    const FetchAssignPermission = async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          setCanAssign(false);
          return;
        }
        const res = await fetch(`${API_PATHS.V1}/organization-config`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) {
          setCanAssign(false);
          return;
        }
        const data = await res.json();
        const allow = Array.isArray(data) ? data[0]?.allowTicketSelfAssign : data?.allowTicketSelfAssign;
        setCanAssign(Boolean(allow));
      } catch (err) {
        console.error("Error checking assign permission:", err);
        setCanAssign(false);
      }
    };

    FetchAssignPermission();
  }, [currentTicketId, roles.join('|'), ticket]);

  // Create auth headers for API requests
  const createAuthHeaders = () => {
    const token = getAuthToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Function to handle authenticated document downloads
  const downloadDocument = (documentUrl, fileName = 'Document', download = true) => {
    console.log('==== DOCUMENT VIEWER DEBUG ====');
    console.log(`downloadDocument called with:`, { documentUrl, fileName, download });
    
    if (!documentUrl) {
      console.error('Document URL is empty or undefined');
      alert('Cannot view/download document: URL is missing');
      return;
    }
    
    // Initialize variables to store document metadata
    let extractedUid = null;
    let extractedDocType = null;
    
    // Check if the URL contains a pattern like /users/{uid}/documents/{documentType}
    const userDocPattern = /\/users\/([^/]+)\/documents\/([^/]+)/;
    const userDocMatch = documentUrl.match(userDocPattern);
    if (userDocMatch) {
      extractedUid = userDocMatch[1];
      extractedDocType = userDocMatch[2];
      console.log('Extracted from URL - uid:', extractedUid, 'documentType:', extractedDocType);
    }
    
    console.log('Original document URL:', documentUrl);
    
    // Handle different URL formats
    if (typeof documentUrl !== 'string') {
      console.error('Document URL is not a string:', documentUrl);
      alert('Error: Invalid document URL format');
      return;
    }
    
    // Try to parse document data if it's a JSON string
    let parsedDoc = null;
    
    try {
      if (documentUrl.startsWith('{') && documentUrl.endsWith('}')) {
        parsedDoc = JSON.parse(documentUrl);
        console.log('Parsed document data:', parsedDoc);
        if (parsedDoc.uid && parsedDoc.documentType) {
          extractedUid = parsedDoc.uid;
          extractedDocType = parsedDoc.documentType;
          console.log('Extracted from JSON - uid:', extractedUid, 'documentType:', extractedDocType);
        }
      }
    } catch (e) {
      console.log('Not a valid JSON string, continuing with normal URL processing');
    }
    
    // Extract UID and document type from URL if it's a workflow document URL
    if (!extractedUid && !extractedDocType && documentUrl.includes('/users/') && documentUrl.includes('/documents/')) {
      const docRefMatch = documentUrl.match(/\/(?:v1|api)\/users\/([^\/]+)\/documents\/([^\/]+)\/download/);
      if (docRefMatch) {
        extractedUid = docRefMatch[1];
        extractedDocType = docRefMatch[2];
        console.log('Extracted from URL pattern - uid:', extractedUid, 'documentType:', extractedDocType);
      } else {
        const parts = (typeof documentUrl === 'string' && documentUrl.includes('/')) ? documentUrl.split('/') : [];
        if (parts.length >= 6) {
          extractedUid = parts[3];
          extractedDocType = parts[5];
        }
        console.log('Extracted from URL parts - uid:', extractedUid, 'documentType:', extractedDocType);
      }
    }
    
    // Get authentication token
    const token = localStorage.getItem('jwt_token') || 
                 (localStorage.getItem('authInfo') ? 
                  JSON.parse(localStorage.getItem('authInfo')).token : null);
    
    if (!token) {
      console.error('No authentication token found');
      alert('Authentication required. Please log in again.');
      return;
    }
    
    // Determine the correct download URL
    let downloadUrl;
    
    // If this is a direct download request, use the old method
    if (download) {
      
      // 0) If parsedDoc contains documentReferences with filePath, prefer direct file download
      const pickRefFilePath = () => {
        try {
          const refs = parsedDoc?.documentReferences || parsedDoc?.documentReferenceList;
          if (Array.isArray(refs) && refs.length > 0) {
            const primary = refs.find(r => r.isPrimary) || refs[0];
            return primary?.filePath || primary?.path || null;
          }
        } catch {}
        return null;
      };
      const refFilePath = pickRefFilePath();
      if (refFilePath) {
        downloadUrl = `${API_PATHS.V1}/files/download?filePath=${encodeURIComponent(refFilePath)}`;
        console.log('Using filePath from documentReferences for download URL:', downloadUrl);
      } else if (extractedUid && extractedDocType) {
        // 1) If we have uid + docType and not a dynamic field, try the user document endpoint
        const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(extractedDocType);
        if (!isDynamicServiceField) {
          const candUrl = API_PATHS.DOCUMENT_DOWNLOAD(extractedUid, extractedDocType);
          if (candUrl) {
            downloadUrl = candUrl;
            console.log('Using extracted UID and document type for download URL:', downloadUrl);
          } else {
            console.log('DOCUMENT_DOWNLOAD returned null (missing uid or dynamic type). Will try fallback cases.');
          }
        } else {
          console.log('Dynamic service field detected, skipping employee document endpoint for docType:', extractedDocType);
        }
      }
      // Case 1: URL already contains /api/files/download or /v1/files/download endpoint
      else if (documentUrl.includes('/api/files/download') || documentUrl.includes('/v1/files/download')) {
        // This is already a file download URL, just ensure it has the full base URL
        if (documentUrl.startsWith('/')) {
          downloadUrl = `${API_PATHS.BASE}${documentUrl}`;
        } else if (!documentUrl.startsWith('http')) {
          downloadUrl = `${API_PATHS.BASE}/${documentUrl}`;
        } else {
          downloadUrl = documentUrl;
        }
      }
      // Case 3: Check if it's a file path (contains / or file extension) vs document ID
      else {
        // Check if it looks like a file path (contains / or has file extension)
        const isFilePath = documentUrl.includes('/') || /\.[a-zA-Z0-9]+$/.test(documentUrl);
        
        if (isFilePath) {
          // It's a file path - use the file download endpoint
          const encodedPath = encodeURIComponent(documentUrl);
          downloadUrl = `${API_PATHS.BASE}/v1/files/download?filePath=${encodedPath}`;
          console.log('Using file download URL for file path:', downloadUrl);
        } else {
          // It's a document ID - use the document download endpoint
          downloadUrl = `${API_PATHS.BASE}/v1/users/documents/download?documentId=${documentUrl}`;
          console.log('Using document ID download URL for document ID:', downloadUrl);
        }
      }
      
      console.log('Downloading document from:', downloadUrl);
      
      // Make a direct fetch request with authentication
      fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`File download failed: ${response.status} ${response.statusText}`);
        }
        return response.blob();
      })
      .then(blob => {
        // Create a URL for the blob
        const url = window.URL.createObjectURL(blob);
        
        // Create a link to download the file
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || 'document';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch(error => {
        console.error('Error downloading file:', error);
        alert(`Error downloading file: ${error.message}`);
      });
    } else {
      // For viewing, use the document viewer component
      
      // Set downloadUrl for viewing if not already set
      if (!downloadUrl) {
        if (extractedUid && extractedDocType) {
          // Check if this is a dynamic service field (UUID pattern)
          const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(extractedDocType);
          
          if (isDynamicServiceField) {
            // For dynamic service fields, use the original document URL which should contain the file path
            console.log('Dynamic service field detected for viewing, using original URL:', documentUrl);
            downloadUrl = documentUrl;
          } else {
            downloadUrl = API_PATHS.DOCUMENT_DOWNLOAD(extractedUid, extractedDocType);
          }
        } else {
          // Use the original URL if we couldn't extract uid and documentType
          downloadUrl = documentUrl;
        }
      }
      
      console.log('Setting up document viewer with URL:', downloadUrl);
      
      setCurrentDocument({
        url: downloadUrl, // Use the processed download URL, not the original documentUrl
        fileName: fileName || 'Document',
        uid: extractedUid,
        documentType: extractedDocType
      });
      
      console.log('Current document set to:', {
        url: downloadUrl,
        fileName: fileName || 'Document',
        uid: extractedUid,
        documentType: extractedDocType
      });
      
      setDocumentViewerOpen(true);
    }
  };
  
  // Note: ticketId is now defined at the top of the component

  // Function to handle API errors with fallback
  const handleApiError = (error, fallbackAction, errorMessage) => {
    console.error(errorMessage, error);
    setError(error.message || 'An unexpected error occurred');
    
    // Execute fallback action if provided
    if (fallbackAction) {
      fallbackAction();
    }
  };

  // Whether the ticket is in a completed state (completion date is only meaningful/editable then)
  const isTicketCompleted = () =>
    [ticket?.status, ticket?.actualStatus].some((s) => String(s || '').toUpperCase() === 'COMPLETED');

  // Convert epoch seconds -> "YYYY-MM-DD" for a date input (local day)
  const epochToDateInput = (sec) => {
    if (!sec) return '';
    const d = new Date(Number(sec) * 1000);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  // Convert a "YYYY-MM-DD" date input -> epoch seconds (local noon, so it never rolls to the previous day)
  const dateInputToEpoch = (value) => {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;
    return Math.floor(new Date(y, m - 1, d, 12, 0, 0).getTime() / 1000);
  };

  // Open transaction number / completion date edit dialog
  const handleOpenTransactionNumberDialog = () => {
    setEditingTransactionNumber(ticket?.transactionNumber || '');
    setEditingCompletionDate(epochToDateInput(ticket?.completedAt));
    setTransactionNumberDialogOpen(true);
  };

  // Close the edit dialog
  const handleCloseTransactionNumberDialog = () => {
    setTransactionNumberDialogOpen(false);
    setEditingTransactionNumber('');
    setEditingCompletionDate('');
  };

  // Update transaction number
  const handleUpdateTransactionNumber = async () => {
    if (!ticketId) {
      console.error('No ticket ID available');
      return;
    }

    try {
      setUpdatingTransactionNumber(true);
      
      const token = getAuthToken();
      if (!token) {
        console.error('Authentication token not found');
        return;
      }

      // Call PUT /v1/tickets/{ticketId}. Completion date is only sent for completed tickets.
      const payload = { transactionNumber: editingTransactionNumber };
      if (isTicketCompleted() && editingCompletionDate) {
        payload.completedAt = dateInputToEpoch(editingCompletionDate);
      }
      const response = await AppService.put(`v1/ticket/${ticketId}`, payload);

      console.log('Ticket details updated:', response);

      // Update local ticket state with response data
      if (response && response.data) {
        setTicket(response.data);
      } else if (response) {
        setTicket(response);
      }

      // Close dialog
      handleCloseTransactionNumberDialog();
      
      // Show success message (optional)
      console.log('Transaction number updated successfully');
    } catch (error) {
      console.error('Error updating ticket details:', error);
      setError(error?.response?.data?.message || error?.message || 'Failed to update ticket details');
    } finally {
      setUpdatingTransactionNumber(false);
    }
  };

  // Function to fetch ticket details from the API
  const fetchTicketDetails = async (showRefreshing = false) => {
    if (!ticketId) {
      setError('No ticket ID provided.');
      setLoading(false);
      return;
    }
    
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      // Fetch ticket details from API
      const params = {
        ticketId: ticketId
      };
      
      console.log('Fetching ticket details with params:', params);
      
      // Use the authenticated v1 endpoint
      const response = await AppService.get("v1/ticket/details", { params });
      
      // Handle different response formats
      let ticketData = null;
      if (response && response.data) {
        ticketData = response.data;
      } else if (response && typeof response === 'object' && !Array.isArray(response)) {
        ticketData = response;
      }
      
      if (!ticketData) {
        throw new Error('Invalid ticket data format received from API');
      }
      
      console.log('Fetched ticket data:', ticketData);
      
      // Ensure status is properly extracted from the response
      if (ticketData.status) {
        console.log('Ticket status from API:', ticketData.status);
      } else {
        console.log('No status found in ticket data, checking other properties');
        // Look for status in other possible locations
        if (ticketData.ticketStatus) {
          console.log('Found status in ticketStatus property:', ticketData.ticketStatus);
          ticketData.status = ticketData.ticketStatus;
        } else if (typeof ticketData.ticket === 'object' && ticketData.ticket?.status) {
          console.log('Found status in ticket.status property:', ticketData.ticket.status);
          ticketData.status = ticketData.ticket.status;
        }
      }
      
// Process assignment information if it exists
// Handle different API response formats and field naming conventions
if (ticketData.assignedUser || ticketData.assignedOrganisation) {
  console.log('Assignment information directly available in response');
} else if (ticketData.ticket && (ticketData.ticket.assignedUser || ticketData.ticket.assignedOrganisation)) {
  // If the data is nested under a "ticket" property, bring it to the top level
  console.log('Assignment information found in nested ticket object');
  ticketData.assignedUser = ticketData.ticket.assignedUser;
  ticketData.assignedOrganisation = ticketData.ticket.assignedOrganisation;
} else if (ticketData.assignedUserId || ticketData.assignedOrganisationId) {
  // If only IDs are available, we need to build objects
  console.log('Only assignment IDs found, constructing objects');
  
  if (ticketData.assignedUserId && !ticketData.assignedUser) {
    ticketData.assignedUser = {
      id: ticketData.assignedUserId,
      name: ticketData.assignedUserName || 'Assigned User',
      email: ticketData.assignedUserEmail
    };
  }
  
  if (ticketData.assignedOrganisationId && !ticketData.assignedOrganisation) {
    ticketData.assignedOrganisation = {
      id: ticketData.assignedOrganisationId,
      name: ticketData.assignedOrganisationName || 'Assigned Organization'
    };
  }
}

// If we have assignedTo field but not structured assignment objects
if (ticketData.assignedTo && (!ticketData.assignedUser && !ticketData.assignedOrganisation)) {
  console.log('Found assignedTo field:', ticketData.assignedTo);
  
  // Try to determine if this is a user or organization assignment
  if (ticketData.status === 'ASSIGNED_TO_ORGANIZATION') {
    ticketData.assignedOrganisation = {
      name: ticketData.assignedTo,
      id: ticketData.assignedToId
    };
  } else if (ticketData.status === 'ASSIGNED') {
    ticketData.assignedUser = {
      name: ticketData.assignedTo,
      id: ticketData.assignedToId,
      email: ticketData.assignedToEmail
    };
  }
}

// Log the processed assignment information
if (ticketData.assignedUser) {
  console.log('Ticket assigned to user:', ticketData.assignedUser);
}
if (ticketData.assignedOrganisation) {
  console.log('Ticket assigned to organization:', ticketData.assignedOrganisation);
}
// Add processing for claimedBy field if it exists
if (ticketData.claimedBy && !ticketData.assignedUser) {
  console.log('Found claimedBy field:', ticketData.claimedBy);
  
  // Store original claimedBy info to display separately in UI
  ticketData.claimedByUser = {
    id: ticketData.claimedBy.id,
    name: ticketData.claimedBy.name,
    email: ticketData.claimedBy.emailId,
    customerOrganisation: ticketData.claimedBy.customerOrganisation
  };
  
  // Also use it for assignedUser if no assigned user exists
  if (!ticketData.assignedUser) {
    ticketData.assignedUser = {
      id: ticketData.claimedBy.id,
      name: ticketData.claimedBy.name,
      email: ticketData.claimedBy.emailId
    };
  }
  
  // If we have organization information in the claimedBy user
  if (ticketData.claimedBy.organisation) {
    ticketData.assignedOrganisation = ticketData.claimedBy.organisation;
  }
}

// Process created organization info
if (ticketData.customerOrganisation && !ticketData.createdOrganisation) {
  ticketData.createdOrganisation = {
    name: ticketData.customerOrganisation
  };
}
      setTicket(ticketData);
      
      // Set the numeric ID for document operations using the ticketNumber field
      if (ticketData.ticketNumber) {
        console.log(`Using ticket.ticketNumber (${ticketData.ticketNumber}) for fetching documents`);
        
        // Fetch supporting documents if we have a numeric ID
        fetchSupportingDocs(ticketData.ticketNumber.toString());
        
        // Check for additional documents in the response
        if (ticketData.additionalDocuments) {
          console.log('Found additional documents in response:', ticketData.additionalDocuments);
          setAdditionalDocuments(ticketData.additionalDocuments || []);
        } else {
          // Fallback to separate API call if additionalDocuments is not in the response
          fetchAdditionalDocs(ticketData.ticketNumber.toString());
        }
      } else {
        console.warn('No ticketNumber field found in ticket data, document operations may not work');
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      setError('Failed to load ticket details. Please try again.');
      
      // If we have ticket data from location state, use that as fallback
      if (location.state?.ticket) {
        console.log('Using ticket data from location state as fallback');
        setTicket(location.state.ticket);
      } else {
        setTicket(null);
      }
      
      return null;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch Actions by Service
  const fetchAvailableActions = async (serviceIdParam) => {
    try {
      const token = getAuthToken();
      if (!token) {
        console.error('Authentication token not found');
        return;
      }

      const serviceId = serviceIdParam || getServiceIdFromTicket();
      if (!serviceId) {
        console.warn('Service ID not found on ticket; cannot load actions');
        setAvailableActions([]);
        return;
      }

      const apiUrl = `${API_PATHS.V1}/action/getByService/${encodeURIComponent(serviceId)}`;
      const res = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!res.ok) {
        console.error('Failed to fetch actions:', res.status, res.statusText);
        setAvailableActions([]);
        return;
      }
      const data = await res.json();
      const list = Array.isArray(data?.content) ? data.content : (Array.isArray(data) ? data : []);
      setAvailableActions(list);
    } catch (e) {
      console.error('Error fetching available actions:', e);
      setAvailableActions([]);
    }
  };

  // Open/Close handlers for Add Action dialog
  const handleOpenAddActionDialog = async () => {
    setAddActionDialogOpen(true);
    setSelectedActionId('');
    setSelectedAction(null);
    setActionDueDate('');
    setActionRemarks('');
    fetchAvailableActions();
  };
  const handleCloseAddActionDialog = () => {
    setAddActionDialogOpen(false);
  };

  // Open Edit Action dialog with current row data
  const handleOpenEditActionDialog = (row) => {
    if (!row) return;
    const name = row?.action?.name || row?.name || row?.label || row?.code || row?.action || 'Action';
    // Normalize date to YYYY-MM-DD if present
    let due = '';
    try {
      if (row?.dueDate) {
        const d = new Date(row.dueDate);
        if (!isNaN(d.getTime())) {
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          due = `${yyyy}-${mm}-${dd}`;
        }
      }
    } catch {}
    setEditActionData({
      id: row?.id || '',
      name,
      status: row?.status || 'PENDING',
      dueDate: due,
      remarks: row?.remarks || ''
    });
    setEditActionDialogOpen(true);
  };
  const handleCloseEditActionDialog = () => setEditActionDialogOpen(false);

  // Save Edit Action - placeholder (wire to API later if needed)
  const handleSaveEditAction = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        console.error('Authentication token not found');
        return;
      }

      const actionId = editActionData?.id;
      if (!actionId) {
        console.error('No action ID available for update. Open the browser console to inspect the action row payload and adjust ID mapping.');
        return;
      }

      // PUT /v1/ticket/action/{id}
      const apiUrl = `${API_PATHS.V1}/ticket/action/${encodeURIComponent(actionId)}`;
      const body = {
        status: editActionData?.status || 'PENDING',
        dueDate: toUtcISOString(editActionData?.dueDate),
        remarks: editActionData?.remarks || ''
      };

      const res = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        console.error('Failed to update action:', res.status, res.statusText);
        try { const err = await res.text(); console.error(err); } catch {}
        return;
      }

      setEditActionDialogOpen(false);
      // Refresh ticket actions/details
      await fetchTicketDetails(true);
    } catch (e) {
      console.error('Error saving edited action:', e);
    }
  };

  // When action selected, auto-calc due date by current date + action.dueDays
  const handleActionChange = (e) => {
    const value = e.target.value;
    setSelectedActionId(value);
    const act = (availableActions || []).find(a => String(a?.id) === String(value));
    setSelectedAction(act || null);
    try {
      const dueDays = Number(
        (act && (act.dueDays != null ? act.dueDays : act.action?.defaultDueDays)) || 0
      );
      const d = new Date();
      if (!Number.isNaN(dueDays) && dueDays > 0) {
        d.setDate(d.getDate() + dueDays);
      }
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      setActionDueDate(`${yyyy}-${mm}-${dd}`);
    } catch {}
  };

  // Save Add Action -> POST /v1/ticket/{ticketId}/action/{actionId}
  const handleSaveAddAction = async () => {
    try {
      if (!selectedActionId) return;
      const token = getAuthToken();
      if (!token) {
        console.error('Authentication token not found');
        return;
      }
      // Determine ticket id to use in path: prefer currentTicketId from URL; fallback to ticket.id/uid
      const pathTicketId = currentTicketId || ticket?.id || ticket?.uid || ticket?.ticketId || ticket?.ticketUid;
      if (!pathTicketId) {
        console.error('No ticket ID available for posting action');
        return;
      }
      const apiUrl = `${API_PATHS.V1}/ticket/${encodeURIComponent(pathTicketId)}/action/${encodeURIComponent(selectedActionId)}`;
      const body = {
        dueDate: toUtcISOString(actionDueDate),
        remarks: actionRemarks || ''
      };
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        console.error('Failed to post action:', res.status, res.statusText);
        try { const err = await res.text(); console.error(err); } catch {}
        return;
      }
      // refresh ticket details to reflect new action
      await fetchTicketDetails(true);
      handleCloseAddActionDialog();
    } catch (e) {
      console.error('Error saving action:', e);
    }
  };
  
  // Fetch activity logs
  const fetchActivityLogs = async (ticketId) => {
    try {
      const token = getAuthToken();
      
      if (!token) {
        console.error('Authentication token not found');
        return;
      }
      
      // Prefer UUID from URL param; fall back to numeric only if no UUID available
      const isUuid = (v) => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);
      const preferNumeric = (val) => (val !== undefined && val !== null && !isNaN(Number(val)));
      const urlUuid = isUuid(ticketId) ? ticketId : null;
      const numericFromTicket = preferNumeric(ticket?.ticketNumber) ? String(ticket.ticketNumber) : (preferNumeric(ticket?.id) ? String(ticket.id) : null);
      const uidFromTicket = ticket?.uid || ticket?.ticketUid || ticket?.ticketCode || null;
      // Primary effective id stays UUID if present; otherwise numeric or fallback string id
      const effectiveId = urlUuid || (preferNumeric(ticketId) ? String(ticketId) : (numericFromTicket || ticketId || uidFromTicket));
      const candidateUid = uidFromTicket; // do not misuse UUID for UID endpoints

      // Prefer the standardized v1 endpoint (UUID first if available)
      let apiUrl = `${API_PATHS.V1}/tickets/${effectiveId}/logs?page=0&size=50`;
      console.log(`Fetching activity logs from: ${apiUrl}`);
      
      let response = await fetch(
        apiUrl,
        {
          method: 'GET',
          headers: createAuthHeaders()
        }
      );
      
      // If that fails, try the /v1 single form without paging, then legacy /api endpoints
      if (!response.ok) {
        console.log(`First endpoint failed with ${response.status}, trying fallbacks`);
        apiUrl = `${API_PATHS.V1}/tickets/${effectiveId}/logs`;
        console.log(`Trying v1 fallback without paging: ${apiUrl}`);
        response = await fetch(
          apiUrl,
          {
            method: 'GET',
            headers: createAuthHeaders()
          }
        );
      }
      if (!response.ok) {
        apiUrl = `${API_PATHS.API}/tickets/${effectiveId}/logs`;
        console.log(`Trying legacy API endpoint: ${apiUrl}`);
        
        response = await fetch(
          apiUrl,
          {
            method: 'GET',
            headers: createAuthHeaders()
          }
        );
      }
      // If we tried numeric id first and we DO have a UUID from URL, explicitly try UUID path now
      if (!response.ok && urlUuid) {
        apiUrl = `${API_PATHS.V1}/tickets/${urlUuid}/logs`;
        console.log(`Trying v1 with URL UUID: ${apiUrl}`);
        response = await fetch(apiUrl, { method: 'GET', headers: createAuthHeaders() });
      }
      // If still not ok and we have a UID style id, try UID endpoints
      if (!response.ok && candidateUid) {
        // Try v1 UID route
        apiUrl = `${API_PATHS.V1}/tickets/uid/${encodeURIComponent(candidateUid)}/logs`;
        console.log(`Trying v1 UID endpoint: ${apiUrl}`);
        response = await fetch(apiUrl, { method: 'GET', headers: createAuthHeaders() });
      }
      if (!response.ok && candidateUid) {
        // Try legacy API UID route
        apiUrl = `${API_PATHS.API}/tickets/uid/${encodeURIComponent(candidateUid)}/logs`;
        console.log(`Trying legacy API UID endpoint: ${apiUrl}`);
        response = await fetch(apiUrl, { method: 'GET', headers: createAuthHeaders() });
      }
      // As a last resort, try by ticketNumber query if we have it
      if (!response.ok && preferNumeric(ticket?.ticketNumber)) {
        apiUrl = `${API_PATHS.V1}/tickets/logs?ticketNumber=${encodeURIComponent(String(ticket.ticketNumber))}`;
        console.log(`Trying v1 query by ticketNumber: ${apiUrl}`);
        response = await fetch(apiUrl, { method: 'GET', headers: createAuthHeaders() });
      }
      
      if (response.ok) {
        const data = await response.json();
        console.log('Activity logs raw payload:', data);
        let list = [];
        if (Array.isArray(data)) {
          list = data;
        } else if (data && Array.isArray(data.content)) {
          list = data.content;
        } else if (data && Array.isArray(data.data)) {
          list = data.data;
        } else if (data && Array.isArray(data.items)) {
          list = data.items;
        } else if (data && Array.isArray(data.results)) {
          list = data.results;
        }
        console.log(`Fetched ${list.length} activity logs after normalization`);
        setActivityLogs(list);
      } else {
        const errTxt = `${response.status} ${response.statusText}`;
        console.error(`Failed to fetch activity logs: ${errTxt}`);
        setActivityLogs([]);
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      setActivityLogs([]);
    }
  };
  
  // Fetch supporting documents for the ticket
  const fetchSupportingDocs = async (ticketIdParam) => {
    try {
      setLoadingSupportingDocs(true);
      
      const token = getAuthToken();
      
      if (!token) {
        console.error('Authentication token not found');
        setLoadingSupportingDocs(false);
        return;
      }
            
      // Important: For supporting docs, we need to use the NUMERIC ticket ID
      // AdditionalDocumentController expects a Long, not a UUID
      let numericId = null;
      
      // If a specific ticketId is provided, use it
      if (ticketIdParam) {
        numericId = ticketIdParam;
        console.log(`Using provided ticketId (${numericId}) for fetching documents`);
      } 
      // Otherwise try to find a valid ID from the ticket state object
      else if (ticket) {
        // Check for different possible ID fields in order of preference
        if (ticket.ticketNumber) {
          numericId = ticket.ticketNumber;
          console.log(`Using ticket.ticketNumber (${numericId}) for fetching documents`);
        } else if (ticket.id) {
          numericId = ticket.id;
          console.log(`Using ticket.id (${numericId}) for fetching documents`);
        } else if (ticket.ticketId) {
          numericId = ticket.ticketId;
          console.log(`Using ticket.ticketId (${numericId}) for fetching documents`);
        } else if (ticket.uid) {
          // If only UID is available, try to extract numeric part
          const match = ticket.uid.match(/\d+/);
          if (match) {
            numericId = match[0];
            console.log(`Extracted numeric ID (${numericId}) from ticket.uid`);
          } else {
            numericId = ticket.uid;
            console.log(`Using ticket.uid (${numericId}) for fetching documents`);
          }
        } else {
          console.error('Could not find any valid ID field in ticket object for fetching documents');
          console.log('Ticket object for fetch:', ticket);
          setLoadingSupportingDocs(false);
          return; // Exit early if we can't find the ID
        }
      } else {
        console.error('No ticketId provided and ticket object is null or undefined');
        setLoadingSupportingDocs(false);
        return; // Exit early if no ID source is available
      }
      const response = await fetch(
        `${API_PATHS.FILE}/additional-documents/${numericId}`,
        {
          method: 'GET',
          headers: createAuthHeaders()
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log(`Fetched ${data ? data.length : 0} supporting documents`);
        setSupportingDocs(data || []);
      } else {
        console.error(`Failed to fetch supporting documents: ${response.status}`);
        setSupportingDocs([]);
      }
    } catch (error) {
      console.error('Error fetching supporting documents:', error);
      setSupportingDocs([]);
    } finally {
      setLoadingSupportingDocs(false);
    }
  };

  // Initial data loading
  useEffect(() => {
    if (ticketId && ticketId !== 'undefined') {
      // Only fetch ticket details - we won't make calls to non-existing APIs
      fetchTicketDetails();
      
      // Initialize empty arrays for data that would come from non-existing APIs
      setSupportingDocs([]);
      setActivityLogs([]);
      setAdditionalDocuments([]);
      
      // Use a minimal fallback for organizations
      if (auth?.user?.organizationId) {
        const userOrg = {
          id: auth.user.organizationId,
          name: auth.user.organizationName || 'Your Organization'
        };
        setOrganizations([userOrg]);
      } else {
        setOrganizations([]);
      }
    }
  }, [ticketId]);

  // Load activity logs when the History tab is selected
  useEffect(() => {
    try {
      if (activeTab === 'history' && ticketId && ticketId !== 'undefined') {
        fetchActivityLogs(ticketId);
      }
    } catch (e) {
      console.warn('Failed to load activity logs on tab switch:', e?.message || e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, ticketId]);

  // Block navigation when documents are uploaded but status not changed
  useEffect(() => {
    if (!isStatusChanged) {
      const handleClick = (event) => {
        // Check if the click is on a navigation link (sidebar or any other navigation)
        const target = event.target.closest('a[href]');
        
        if (target && target.href) {
          // Don't block if it's the same page or a hash link
          const currentPath = window.location.pathname;
          const targetUrl = new URL(target.href, window.location.origin);
          
          // Only block if navigating to a different page
          if (targetUrl.pathname !== currentPath && !target.href.startsWith('#')) {
            // Prevent default navigation
            event.preventDefault();
            event.stopPropagation();
            
            // Store the intended destination
            setPendingNavigation(target.href);
            
            // Show confirmation dialog
            setShowBackConfirmDialog(true);
          }
        }
      };

      // Add click listener to document with capture phase to catch it early
      document.addEventListener('click', handleClick, true);

      return () => {
        document.removeEventListener('click', handleClick, true);
      };
    }
  }, [isStatusChanged]);

  // Warn user before closing/refreshing browser tab [enable if needed]
  // useBeforeUnload(
  //   React.useCallback(
  //     (event) => {
  //       if (!isStatusChanged) {
  //         event.preventDefault();
  //         event.returnValue = '';
  //       }
  //     },
  //     [isStatusChanged]
  //   )
  // );

  // Function to handle status change
  const handleStatusChange = async (newStatus) => {
    try {
      console.log('Status changed to:', newStatus);
      
      setIsStatusChanged(true);
      
      // Update ticket locally first for immediate UI feedback
      setTicket(prevTicket => {
        const updatedTicket = { ...prevTicket, status: newStatus };
        console.log('Updated ticket locally:', updatedTicket);
        return updatedTicket;
      });
      
      // Refresh the ticket data to get the latest status
      const params = { ticketId: ticketId };
      try {
        const response = await AppService.get("v1/ticket/details", { params });
        if (response) {
          let ticketData = null;
          if (response && response.data) {
            ticketData = response.data;
          } else if (response && typeof response === 'object') {
            ticketData = response;
          }
          
          if (ticketData) {
            console.log('Refreshed ticket data after status change:', ticketData);
            
            // Make sure the status is correctly set
            if (!ticketData.status && newStatus) {
              ticketData.status = newStatus;
            }
            
            setTicket(ticketData);
          }
        }
      } catch (refreshError) {
        console.error('Error refreshing ticket data:', refreshError);
        // Keep the locally updated status if refresh fails
      }
    } catch (err) {
      console.error('Error handling status change:', err);
    }
  };

  // Function to handle refresh button click
  const handleRefresh = () => {
    fetchTicketDetails(true);
  };

  // Function to handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };
  
  // Function to handle closing the assignment dialog
  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
  };

  // Function to handle back button click
  const handleBack = () => {
    // Check if documents were uploaded but status wasn't changed
    if (!isStatusChanged) {
      setShowBackConfirmDialog(true);
    } else {
      navigate(-1);
    }
  };

  // Function to confirm leaving without status change
  const handleConfirmLeave = () => {
    setShowBackConfirmDialog(false);
    if (pendingNavigation) {
      // If there's a pending navigation (from sidebar click), execute it
      window.location.href = pendingNavigation;
    } else {
      // Otherwise, just go back
      navigate(-1);
    }
    setPendingNavigation(null);
  };

  // Function to cancel leaving
  const handleCancelLeave = () => {
    setShowBackConfirmDialog(false);
    setPendingNavigation(null);
    if(isAssignedToMe){
      setStatusDialogOpen(true);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (error) {
      return 'Invalid Date';
    }
  };
  
  // Parse document field value to extract document URL and other metadata
  const parseDocumentFieldValue = (value) => {
    console.log('Parsing document field value:', value);
    
    // Handle null or undefined values
    if (!value) return null;
    
    // If value is a string, check if it's a stringified JSON or object string
    if (typeof value === 'string') {
      try {
        // Try to parse as JSON first
        const parsedValue = JSON.parse(value);
        console.log('Parsed document field from JSON string:', parsedValue);
        return parseDocumentFieldValue(parsedValue); // Recursively parse the parsed value
      } catch (e) {
        // Check if it's a string representation of an object (like {target={...}})
        if (value.includes('target=') && value.includes('filePath=')) {
          console.log('Detected object string format, extracting filePath:', value);
          // Extract filePath from the string using regex
          const filePathMatch = value.match(/filePath=([^,}]+)/);
          if (filePathMatch) {
            const filePath = filePathMatch[1];
            console.log('Extracted filePath from object string:', filePath);
            
            // Also try to extract fileName
            const fileNameMatch = value.match(/fileName=([^,}]+)/);
            const fileName = fileNameMatch ? fileNameMatch[1] : lastSegment(filePath);
            
            return {
              documentUrl: filePath,
              fileName: fileName,
              title: fileName,
              fieldType: 'document'
            };
          }
        }
        
        // Check if it's a UUID
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidPattern.test(value)) {
          return {
            documentUrl: value, // Just the UUID
            fileName: 'Document', // Default name since we only have UUID
            title: 'Document',
            fieldType: 'document',
            isUuid: true
          };
        }
        
        // If it's a document reference URL
        if (value.includes('/users/') && value.includes('/documents/')) {
          const docRefMatch = value.match(/\/(?:v1|api)\/users\/([^\/]+)\/documents\/([^\/]+)\/download/);
          if (docRefMatch) {
            const uid = docRefMatch[1];
            const documentType = docRefMatch[2];
            const fileName = lastSegment(value, documentType) || documentType;
            
            return {
              documentUrl: value,
              documentType,
              uid,
              fileName,
              isReference: true
            };
          }
        }
        
        // Not a JSON string or special format, treat as direct URL
        return {
          documentUrl: value,
          fileName: lastSegment(value) || 'Document',
          title: lastSegment(value) || 'Document',
          fieldType: 'document'
        };
      }
    }
    
    // If value is an object with isReference=true, it's a document reference
    if (value.isReference === true) {
      console.log('Document field is a reference:', value);
      
      // Check if this is a dynamic service field (UUID pattern)
      const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.documentType);
      
      let documentUrl = value.documentUrl;
      if (!documentUrl && !isDynamicServiceField) {
        // Only construct employee document URL for static document fields
        documentUrl = `/v1/users/${value.uid}/documents/${value.documentType}/download`;
      }
      
      return {
        documentUrl,
        fileName: value.fileName || 'Document',
        title: value.fileName || value.title || 'Document',
        documentType: value.documentType,
        uid: value.uid,
        fieldType: 'document',
        isReference: true
      };
    }
    
    // If value has documentUrl property, extract it
    if (value.documentUrl) {
      console.log('Document field has documentUrl:', value);
      return {
        documentUrl: value.documentUrl,
        fileName: value.fileName || value.title || 'Document',
        title: value.title || value.fileName || 'Document',
        documentType: value.documentType,
        uid: value.uid,
        fieldType: 'document'
      };
    }
    
    // If value has target.value, extract from there (workflow format)
    if (value.target && value.target.value) {
      console.log('Document field has target.value:', value.target.value);
      return parseDocumentFieldValue(value.target.value);
    }
    
    // If value has filePath property (direct file reference)
    if (value.filePath) {
      console.log('Document field has filePath:', value);
      return {
        documentUrl: value.filePath,
        fileName: value.fileName || value.title || lastSegment(value.filePath) || 'Document',
        title: value.title || value.fileName || lastSegment(value.filePath) || 'Document',
        fieldType: 'document',
        contentType: value.contentType,
        fileSize: value.fileSize
      };
    }
    
    // If value has id property (could be a file ID)
    if (value.id) {
      console.log('Document field has id:', value);
      // Check if id is a UUID
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (uuidPattern.test(value.id)) {
        return {
          documentUrl: value.id,
          fileName: value.fileName || value.title || 'Document',
          title: value.title || value.fileName || 'Document',
          fieldType: 'document',
          isUuid: true
        };
      }
      return {
        documentUrl: value.id,
        fileName: value.fileName || value.title || 'Document',
        title: value.title || value.fileName || 'Document',
        fieldType: 'document'
      };
    }
    
    // If it's a string that looks like an object representation
    if (typeof value === 'string' && 
        (value.includes('isReference=true') || 
         value.includes('documentType=') || 
         value.includes('documentUrl='))) {
      
      try {
        // Convert string representation to object
        const docObj = {};
        // Extract key-value pairs
        const pairs = (value || '').replace(/[{}]/g, '').split(', ');
        pairs.forEach(pair => {
          if (!pair || typeof pair !== 'string') return;
          const idx = pair.indexOf('=');
          if (idx === -1) return;
          const key = pair.substring(0, idx);
          const val = pair.substring(idx + 1);
          if (key && val) {
            docObj[key.trim()] = val.trim();
          }
        });
        
        // Ensure documentUrl is properly formatted
        if (docObj.documentUrl && !docObj.documentUrl.startsWith('/v1/') && !docObj.documentUrl.startsWith('http')) {
          // If UID and documentType are available, construct a proper document URL
          if (docObj.uid && docObj.documentType) {
            // Check if this is a dynamic service field (UUID pattern)
            const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(docObj.documentType);
            
            if (!isDynamicServiceField) {
              // Only construct employee document URL for static document fields
              docObj.documentUrl = `/v1/users/${docObj.uid}/documents/${docObj.documentType}/download`;
            }
            // For dynamic service fields, leave documentUrl as is (should contain file path)
          }
        }
        
        return docObj;
      } catch (error) {
        console.error('Error parsing document field:', error);
        return null;
      }
    }
    
    return null;
  };
  
  // Helper function to determine if a field is a document type
  const isDocumentField = (field) => {
    if (!field) return false;
    
    // Log the field for debugging
    console.log('Checking if field is document type:', field);
    
    const type = field?.type?.toUpperCase() || '';
    const value = typeof field?.value === 'string' ? field.value.toLowerCase() : '';
    
    // Check type first
    if (type === 'DOCUMENT' || type === 'PDF' || type === 'FILE' || type === 'IMAGE') {
      console.log('Field is document type by type:', type);
      return true;
    }

    // A "not found in employee documents" placeholder has no real file — treat it as a document so
    // it stays out of the plain fields grid (it is filtered from the documents section below).
    if (value.includes('notfoundinemployeedocs')) {
      return true;
    }

    // Check by field code/name for common document fields. Ticket fields carry their identifier in
    // label/category (code is often null), so fall back to those too.
    const fieldCode = (field.code || field.codeString || field.label || field.category || '').toLowerCase();
    const documentFieldCodes = [
      'passportcopy', 'visapage', 'emiratesid', 'photo', 'birthcertificate',
      'salarycertificate', 'labourcontract', 'document', 'attachment', 'file',
      'resume', 'educationalcertificate', 'experiencecertificate', 'policeclearancecertificate'
    ];
    
    if (documentFieldCodes.includes(fieldCode)) {
      console.log('Field is document type by code:', fieldCode);
      return true;
    }
    
    // Check if value is a document reference string
    if (typeof field.value === 'string') {
      if (field.value.includes('isReference=true') || 
          field.value.includes('documentType=') || 
          field.value.includes('documentUrl=')) {
        console.log('Field is document type by reference string');
        return true;
      }
      
      // Check for UUID pattern which might be a file reference
      const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
      if (uuidPattern.test(field.value)) {
        console.log('Field is document type by UUID pattern');
        return true;
      }
    }
    
    // Check if value is an object with document-related properties
    if (typeof field.value === 'object' && field.value !== null) {
      if (field.value.isReference === true || 
          field.value.documentUrl || 
          field.value.filePath || 
          (field.value.fileName && (field.value.id || field.value.documentType))) {
        console.log('Field is document type by object properties');
        return true;
      }
    }
    
    // Check value if type is not explicit
    const isDocByExtension = value.includes('ticket_documents/') || 
           value.endsWith('.pdf') || 
           value.endsWith('.jpg') || 
           value.endsWith('.jpeg') || 
           value.endsWith('.png') || 
           value.endsWith('.doc') || 
           value.endsWith('.docx') || 
           value.endsWith('.xls') || 
           value.endsWith('.xlsx');
    
    if (isDocByExtension) {
      console.log('Field is document type by file extension');
      return true;
    }
    
    return false;
  };
  
  // Document section rendering
  const renderDocumentsSection = () => {
    // "Not found in employee documents" placeholders are documents-by-classification but have no
    // real file, so keep them out of the documents section (they'd render as broken entries).
    const isNotFoundPlaceholder = (field) =>
      typeof field?.value === 'string' && field.value.toLowerCase().includes('notfoundinemployeedocs');

    // Get document fields from regular fields
    const documentFields = ticket?.fields ?
      ticket.fields.filter(field => isDocumentField(field) && !isNotFoundPlaceholder(field)) : [];

    // Get document fields from static fields
    const staticDocumentFields = ticket?.staticFields ?
      ticket.staticFields.filter(field => isDocumentField(field) && !isNotFoundPlaceholder(field)) : [];
    
    // Get supporting documents
    const supportingDocs = ticket?.supportingDocs || [];
    
    // Get additional documents (prefer component state which we refresh after uploads)
    // Fallback to ticket payload if state is empty
    const additionalDocsList = (additionalDocuments && additionalDocuments.length > 0)
      ? additionalDocuments
      : (ticket?.additionalDocuments || []);
    
    // Get employee documents from API response (new system)
    const employeeDocuments = (ticket?.employeeDocuments || []).map(doc => ({
      documentUrl: doc.filePath, // use file path; viewer builds download URL for paths
      title: doc.documentType || 'Employee Document',
      fileName: doc.fileName || lastSegment(doc.filePath) || 'Document',
      uid: doc.uid,
      documentType: doc.documentType,
      fieldType: 'employee',
      isUuid: true,
      originalDoc: doc
    }));
    
    console.log('Document fields found:', {
      documentFields,
      staticDocumentFields,
      supportingDocs,
      additionalDocuments
    });
    
    // Process regular document fields
    const processedDocumentFields = documentFields.map(field => {
      // Parse document field value if it's a string representation
      const parsedDocData = parseDocumentFieldValue(field.value);
      console.log(`Parsed document data for field ${field.code || field.id}:`, parsedDocData);
      console.log(`Original field value for ${field.code || field.id}:`, field.value);
      console.log(`Field value type:`, typeof field.value);
      
      // Get document URL - either from parsed data or directly from field value
      let documentUrl = parsedDocData ? parsedDocData.documentUrl : field.value;
      console.log(`Document URL for ${field.code || field.id}:`, documentUrl);
      
      // Handle document URLs that need to be constructed
      if (parsedDocData && parsedDocData.uid && parsedDocData.documentType && 
          (!documentUrl || !documentUrl.includes('/documents/'))) {
        // Check if this is a dynamic service field (UUID pattern)
        const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(parsedDocData.documentType);
        
        if (!isDynamicServiceField) {
          // Only construct employee document URL for static document fields
          documentUrl = `/v1/users/${parsedDocData.uid}/documents/${parsedDocData.documentType}/download`;
        }
        // For dynamic service fields, leave documentUrl as is (should contain file path)
      }
      
      // Get file name from parsed data if available
      const fileName = parsedDocData ? 
        (parsedDocData.fileName || parsedDocData.originalName || lastSegment(documentUrl) || 'Document') : 
        (field.fileName || field.name || lastSegment(documentUrl) || 'Document');
        
      return {
        documentUrl,
        title: field.title || field.label || 'Document',
        fileName,
        documentType: parsedDocData?.documentType || field.type || '',
        uid: parsedDocData?.uid || '',
        code: field.code || field.id || '',
        fieldType: 'regular',
        isReference: parsedDocData?.isReference === true,
        isUuid: parsedDocData?.isUuid === true
      };
    });
    
    // Process static document fields
    const processedStaticFields = staticDocumentFields.map(field => {
      // Parse document field value if it's a string representation
      const parsedDocData = parseDocumentFieldValue(field.value);
      console.log(`Parsed static document data for field ${field.code || field.codeString}:`, parsedDocData);
      console.log(`Original static field value for ${field.code || field.codeString}:`, field.value);
      console.log(`Static field value type:`, typeof field.value);
      
      // Get document URL - either from parsed data or directly from field value
      let documentUrl = parsedDocData ? parsedDocData.documentUrl : field.value;
      console.log(`Static document URL for ${field.code || field.codeString}:`, documentUrl);
      
      // Handle document URLs that need to be constructed
      if (parsedDocData && parsedDocData.uid && parsedDocData.documentType && 
          (!documentUrl || !documentUrl.includes('/documents/'))) {
        // Check if this is a dynamic service field (UUID pattern)
        const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(parsedDocData.documentType);
        
        if (!isDynamicServiceField) {
          // Only construct employee document URL for static document fields
          documentUrl = `/v1/users/${parsedDocData.uid}/documents/${parsedDocData.documentType}/download`;
        }
        // For dynamic service fields, leave documentUrl as is (should contain file path)
      }
      
      // Get file name from parsed data if available
      const fileName = parsedDocData ? 
        (parsedDocData.fileName || parsedDocData.originalName || lastSegment(documentUrl) || 'Document') : 
        (field.fileName || field.name || lastSegment(documentUrl) || 'Document');
        
      return {
        documentUrl,
        title: field.title || field.label || field.code || field.codeString || 'Document',
        fileName,
        documentType: parsedDocData?.documentType || field.type || '',
        uid: parsedDocData?.uid || '',
        code: field.code || field.codeString || '',
        fieldType: 'static',
        isReference: parsedDocData?.isReference === true,
        isUuid: parsedDocData?.isUuid === true
      };
    });
    
    // Process additional documents
    const processedAdditionalDocs = (additionalDocsList || []).map(doc => ({
      documentUrl: doc.filePath,
      title: doc.name || 'Additional Document',
      fileName: doc.originalName || lastSegment(doc.filePath) || 'Document',
      uploadedBy: doc.uploadedBy,
      timestamp: doc.timestamp,
      category: doc.category,
      code: doc.id ? `AD-${doc.id}` : '',
      id: doc.id,
      fieldType: 'additional',
      originalDoc: doc,
      isUuid: true // Additional documents are typically UUIDs
    }));
    
    // Employee documents (linked, via documentGroupId) + additional documents, plus dynamic /
    // field-level uploads that carry a real file path and are NOT already an employee-document
    // reference (those come through employeeDocuments) — so directly-uploaded workflow docs also show.
    const allDocuments = [
      ...(employeeDocuments || []),
      ...processedAdditionalDocs,
      ...processedDocumentFields.filter(d => d.documentUrl && !d.isReference),
      ...processedStaticFields.filter(d => d.documentUrl && !d.isReference)
    ].filter(doc => doc && doc.documentUrl);

    
    if (!allDocuments.length) {
      return (
        <Alert severity="info" sx={{ mt: 2, borderRadius: 1 }}>
          No documents attached to this ticket
        </Alert>
      );
    }

    // Group documents by type (prefer documentType; fallback to title)
    const grouped = allDocuments.reduce((acc, doc) => {
      const key = (
        doc.fieldType === 'additional'
          ? (doc.category || doc.title || 'Others')
          : (doc.documentType || doc.title || 'Others')
      ).toString();
      if (!acc[key]) acc[key] = [];
      acc[key].push(doc);
      return acc;
    }, {});

    const groupKeys = Object.keys(grouped).sort();

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#064d51', mb: 2 }}>
          Attached Documents
        </Typography>

        {groupKeys.map((group) => (
          <Box key={group} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
              {group}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1.5
            }}>
              {grouped[group].map((doc, index) => {
            // Determine file icon based on extension
            let icon = <InsertDriveFileIcon sx={{ color: '#1976d2' }} />;
            let fileType = 'File';
            
            // Determine document source type
            const isWorkflowDoc = doc.isReference || 
                               (doc.documentUrl && 
                                doc.documentUrl.includes('/users/') && 
                                doc.documentUrl.includes('/documents/'));
            
            const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
            const isStandaloneDoc = doc.isUuid || 
                                 (!isWorkflowDoc && 
                                  (doc.documentUrl && 
                                   (doc.documentUrl.includes('/file/download') || 
                                    uuidPattern.test(doc.documentUrl))));
            
            // Set document source label and color
            let sourceLabel = '';
            let sourceColor = '#1976d2';
            
            if (isWorkflowDoc) {
              sourceLabel = 'Workflow Document';
              sourceColor = '#7b1fa2'; // Purple for workflow docs
            } else if (isStandaloneDoc) {
              sourceLabel = 'Ticket Document';
              sourceColor = '#0288d1'; // Blue for standalone docs
            } else if (doc.fieldType === 'additional') {
              sourceLabel = 'Additional Document';
              sourceColor = '#00796b'; // Teal for additional docs
            }
            
            // Determine file type icon based on extension (use safe filename extraction)
            const baseName = (doc.fileName || lastSegment(doc.documentUrl || ''));
            const fileExt = (typeof baseName === 'string' ? baseName.toLowerCase().split('.').pop() : '');
            
            if (['pdf'].includes(fileExt)) {
              icon = <PictureAsPdfIcon sx={{ color: '#e53935' }} />;
              fileType = 'PDF';
            } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileExt)) {
              icon = <ImageIcon sx={{ color: '#43a047' }} />;
              fileType = fileExt.toUpperCase() || 'Image';
            } else if (['doc', 'docx'].includes(fileExt)) {
              icon = <DescriptionIcon sx={{ color: '#1565c0' }} />;
              fileType = 'DOC';
            } else if (['xls', 'xlsx', 'csv'].includes(fileExt)) {
              icon = <TableChartIcon sx={{ color: '#ff9800' }} />;
              fileType = 'Spreadsheet';
            }
            
            // Extract filename for display (safe)
            const fileName = lastSegment(doc.documentUrl, 'Document');
            const displayName = doc.title || doc.fileName || fileName;
            
                return (
                  <Paper
                    key={`${group}-${index}`}
                    elevation={0}
                    sx={{
                      display: 'flex', 
                      alignItems: 'center', 
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        backgroundColor: '#f5f9fa',
                        boxShadow: '0 2px 4px rgba(6, 77, 81, 0.1)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      width: 40, 
                      height: 40, 
                      justifyContent: 'center',
                      mr: 2,
                      backgroundColor: '#f5f5f5',
                      borderRadius: 1
                    }}>
                      {icon}
                    </Box>
                    
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#333', fontSize: '0.95rem' }}>
                        {displayName}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                        {/* File type */}
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                          {fileType}
                        </Typography>
                        
                        {/* Document source type chip */}
                        {sourceLabel && (
                          <Chip 
                            size="small" 
                            label={sourceLabel} 
                            sx={{ 
                              height: 20, 
                              fontSize: '0.7rem', 
                              backgroundColor: `${sourceColor}20`, // 20% opacity
                              color: sourceColor,
                              mr: 1
                            }} 
                          />
                        )}
                        
                        {/* Display file name if available and different from title */}
                        {doc.fileName && doc.fileName !== displayName && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {doc.fileName}
                          </Typography>
                        )}
                        
                        {/* Additional document metadata */}
                        {doc.fieldType === 'additional' && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {doc.category ? `Category: ${doc.category}` : ''}
                            {doc.category ? ' • ' : ''}
                            Uploaded by {doc.uploadedBy || 'Unknown User'}
                            {doc.timestamp && !isNaN(new Date(doc.timestamp)) && ` • ${new Date(doc.timestamp).toLocaleString()}`}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          console.log('View document clicked for:', doc);
                          console.log('Document URL type:', typeof doc.documentUrl);
                          console.log('Document URL value:', doc.documentUrl);
                          // The third parameter should be false for viewing (not downloading)
                          downloadDocument(doc.documentUrl, doc.fileName || doc.name || 'Document', false);
                        }} 
                        title="View document"
                        sx={{ color: '#064d51' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          console.log('Download document clicked for:', doc);
                          console.log('Document URL type:', typeof doc.documentUrl);
                          console.log('Document URL value:', doc.documentUrl);
                          downloadDocument(doc.documentUrl, doc.fileName || doc.name || 'Document', true);
                        }} 
                        title="Download document"
                        sx={{ color: '#064d51' }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                      {/* Only show delete button for additional documents */}
                      {doc.fieldType === 'additional' && (
                        <IconButton 
                          size="small" 
                          onClick={() => confirmDeleteDocument(doc)} 
                          title="Delete document"
                          sx={{ color: '#d32f2f' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };
  
  const renderActivityLogs = () => {
    if (!activityLogs || activityLogs.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 400 }}>
            No activity logs available for this ticket
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Activity History
        </Typography>
        
        <Box sx={{ 
          borderLeft: '2px solid #e0e0e0', 
          pl: 2, 
          ml: 2,
          position: 'relative'
        }}>
          {activityLogs.map((log, index) => (
            <Box 
              key={index} 
              sx={{ 
                position: 'relative',
                pb: 3,
                '&:last-child': { pb: 0 }
              }}
            >
              <Box 
                sx={{ 
                  position: 'absolute',
                  left: -26,
                  top: 0,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: '#064d51',
                  border: '2px solid white'
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {formatDate((log.occurredAt || log.createdAt || log.timestamp))}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {log.actionSummary || log.message || log.activity || 'N/A'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                by {log.actorName || log.userName || log.user || 'System'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // Handle additional document file selection
  const handleAdditionalDocFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setAdditionalDocFile(file);
      
      // Get filename without extension
      const fileName = (typeof file.name === 'string' ? file.name.split('.')[0] : 'document');
      
      // Get file extension
      const fileExtension = (typeof file.name === 'string' ? file.name.split('.').pop().toLowerCase() : '');
      
      // Get MIME type
      const mimeType = file.type;
      
      // Auto-detect document type based on extension
      let documentType = 'GENERAL';
      if (fileExtension === 'pdf') {
        documentType = 'PDF';
      } else if (['doc', 'docx'].includes(fileExtension)) {
        documentType = 'DOCUMENT';
      } else if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) {
        documentType = 'IMAGE';
      } else if (['xls', 'xlsx', 'csv'].includes(fileExtension)) {
        documentType = 'SPREADSHEET';
      } else if (['ppt', 'pptx'].includes(fileExtension)) {
        documentType = 'PRESENTATION';
      }
      
      console.log(`File selected: ${file.name}, MIME type: ${mimeType}, Size: ${(file.size / 1024).toFixed(2)} KB`);
      
      // Update metadata with detected information
      setAdditionalDocMeta(prev => ({
        ...prev,
        name: fileName,
        type: documentType
      }));
    }
  };

  // === Employee document helpers for ticket context ===

  // Helper: sleep/delay
  const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

  // Helper: format date for display in confirmation dialogs
  const formatDateForDialog = (dateString) => {
    if (!dateString) return 'N/A';
    const str = String(dateString).trim();
    const ymd = /^\d{4}-\d{2}-\d{2}$/;
    let date;
    if (ymd.test(str)) {
      const [y, m, d] = str.split('-').map(Number);
      date = new Date(y, m - 1, d);
    } else {
      date = new Date(str);
    }
    if (!date || isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Helper: extract document details via binary EXTRACT endpoint (multipart/form-data)
  const extractDocumentDetailsBinary = async ({ file, processingType, documentIndex = 0 }) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('documentType', processingType);
    fd.append('documentIndex', String(documentIndex));
    const ex = await apiService.api.post(
      API_ENDPOINTS.DOCUMENT_PROCESSING.EXTRACT,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return ex?.data || {};
  };

  // Helper: bulk save employee documents with ticketId for source tracking
  const bulkSaveEmployeeDocuments = async ({ uid, backendDocType, docs, sharedMetadata, ticketUuid }) => {
    const url = `${API_BASE_V1}/employee-documents/${encodeURIComponent(uid)}/bulk`;
    const payload = {
      documentType: backendDocType,
      replaceExisting: false,
      sharedMetadata,
      documents: docs,
      ticketId: ticketUuid || undefined
    };
    console.log('[TicketDetail][EmployeeDocBulkSave] start', { uid, backendDocType, count: docs?.length, ticketUuid, url });
    try {
      const resp = await apiService.api.post(url, payload);
      console.log('[TicketDetail][EmployeeDocBulkSave] success', resp?.data);
      return resp?.data;
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      const errText = data
        ? (typeof data === 'string' ? data : JSON.stringify(data))
        : (e?.message || 'unknown error');
      throw new Error(`Bulk save failed${status ? ` (${status})` : ''}: ${errText}`);
    }
  };

  // Process employee document flow: extract via binary endpoint, bulk save with metadata, show diff dialogs
  const processEmployeeDocumentUpload = async ({ file, docTypeCode, filePath, fileName, fileSize }) => {
    if (!employeeUid || !filePath) return;
    setEmployeeDocUploading(true);
    dispatch(showLoader('Processing document...'));
    try {
      // Normalize backend doc type (snake_case)
      const backendDocType = String(docTypeCode).toLowerCase().replace(/[\s-]/g, '_');
      const processingType = toProcessingDocumentType(docTypeCode);

      // Attempt extraction for supported document types
      let sharedMetadata;
      let extractionResult = null;
      if (file && isExtractionSupported(processingType)) {
        try {
          extractionResult = await extractDocumentDetailsBinary({ file, processingType });
          console.log('[TicketDetail][EmployeeDoc] Extraction result:', extractionResult);
          sharedMetadata = buildSharedMetadataFromExtraction(backendDocType, extractionResult);
        } catch (exErr) {
          console.warn('[TicketDetail][EmployeeDoc] Extraction failed (non-blocking):', exErr);
        }
      }

      // Build bulk save params
      const docs = [{
        filePath: typeof filePath === 'string' ? filePath : String(filePath),
        fileName: fileName || 'document',
        contentType: undefined,
        fileSize: fileSize || undefined
      }];
      const bulkParams = {
        uid: employeeUid,
        backendDocType,
        docs,
        sharedMetadata,
        ticketUuid: ticket?.id
      };

      // For passport/OHC/DL: compute diffs using sharedMetadata and defer bulk save if diffs found
      if (sharedMetadata && employeeData) {
        if (processingType === 'PASSPORT_COPY') {
          const diffs = [];
          const upd = {};
          const pNum = sharedMetadata.documentNumber;
          if (pNum && pNum !== employeeData.passportNumber) {
            diffs.push({ field: 'Passport Number', current: employeeData.passportNumber || 'N/A', next: pNum, key: 'passportNumber' });
            upd.passportNumber = pNum;
          }
          const pExp = sharedMetadata.expiryDate;
          if (pExp && pExp !== employeeData.passportExpiry) {
            diffs.push({ field: 'Passport Expiry', current: employeeData.passportExpiry || 'N/A', next: pExp, key: 'passportExpiry' });
            upd.passportExpiry = pExp;
          }
          const pIss = sharedMetadata.issueDate;
          if (pIss && pIss !== employeeData.passportIssue) {
            diffs.push({ field: 'Passport Issue Date', current: employeeData.passportIssue || 'N/A', next: pIss, key: 'passportIssue' });
            upd.passportIssue = pIss;
          }
          if (diffs.length > 0) {
            pendingBulkSaveRef.current = bulkParams;
            setPassportExtractedNew({ diffs });
            setPassportUpdatePayload(upd);
            setPassportUpdatePromptOpen(true);
            return;
          }
        } else if (processingType === 'OHC') {
          const diffs = [];
          const upd = {};
          const oIss = sharedMetadata.issueDate;
          if (oIss && oIss !== employeeData.ohcIssueDate) {
            diffs.push({ field: 'OHC Issue Date', current: employeeData.ohcIssueDate || 'N/A', next: oIss, key: 'ohcIssueDate' });
            upd.ohcIssueDate = oIss;
          }
          const oExp = sharedMetadata.expiryDate;
          if (oExp && oExp !== employeeData.ohcExpiryDate) {
            diffs.push({ field: 'OHC Expiry Date', current: employeeData.ohcExpiryDate || 'N/A', next: oExp, key: 'ohcExpiryDate' });
            upd.ohcExpiryDate = oExp;
          }
          if (diffs.length > 0) {
            pendingBulkSaveRef.current = bulkParams;
            setOhcExtractedNew({ diffs });
            setOhcUpdatePayload(upd);
            setOhcUpdatePromptOpen(true);
            return;
          }
        } else if (processingType === 'DRIVING_LICENSE') {
          const diffs = [];
          const upd = {};
          const dlIss = sharedMetadata.issueDate;
          if (dlIss && dlIss !== employeeData.drivingLicenseIssueDate) {
            diffs.push({ field: 'Issue Date', current: employeeData.drivingLicenseIssueDate || 'N/A', next: dlIss, key: 'drivingLicenseIssueDate' });
            upd.drivingLicenseIssueDate = dlIss;
          }
          const dlExp = sharedMetadata.expiryDate;
          if (dlExp && dlExp !== employeeData.drivingLicenseExpiryDate) {
            diffs.push({ field: 'Expiry Date', current: employeeData.drivingLicenseExpiryDate || 'N/A', next: dlExp, key: 'drivingLicenseExpiryDate' });
            upd.drivingLicenseExpiryDate = dlExp;
          }
          const dlPlace = sharedMetadata.placeOfIssue;
          if (dlPlace && dlPlace !== employeeData.drivingLicensePlaceOfIssue) {
            diffs.push({ field: 'Place of Issue', current: employeeData.drivingLicensePlaceOfIssue || 'N/A', next: dlPlace, key: 'drivingLicensePlaceOfIssue' });
            upd.drivingLicensePlaceOfIssue = dlPlace;
          }
          if (diffs.length > 0) {
            pendingBulkSaveRef.current = bulkParams;
            setDrivingLicenseExtractedNew({ diffs });
            setDrivingLicenseUpdatePayload(upd);
            setDrivingLicenseUpdatePromptOpen(true);
            return;
          }
        }
        // EMIRATES_ID and VISA_PAGE: no profile diff dialog needed, save immediately
      }

      // No diffs or non-diff doc types: bulk save immediately
      await bulkSaveEmployeeDocuments(bulkParams);

      console.log('[TicketDetail][EmployeeDoc] Employee document flow complete for', backendDocType);
    } catch (err) {
      console.error('[TicketDetail][EmployeeDoc] Error in employee document flow:', err);
    } finally {
      setEmployeeDocUploading(false);
      dispatch(dismissLoader());
    }
  };

  // Confirmation dialog handlers: apply extracted data to employee profile
  const applyPassportUpdates = async () => {
    if (!passportUpdatePayload || !employeeData?.id) { setPassportUpdatePromptOpen(false); return; }
    try {
      const pending = pendingBulkSaveRef.current;
      if (pending) {
        pendingBulkSaveRef.current = null;
        await bulkSaveEmployeeDocuments(pending);
      }
      const payload = buildEmployeeUpdatePayloadFromPassport(passportUpdatePayload);
      const token = getAuthToken();
      await fetch(`${API_PATHS.V1}/employees/${employeeData.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      setEmployeeData(prev => prev ? { ...prev, ...passportUpdatePayload } : prev);
    } catch (e) {
      console.error('[TicketDetail] Failed to apply passport updates:', e);
    } finally {
      setPassportUpdatePromptOpen(false);
      setPassportExtractedNew(null);
      setPassportUpdatePayload(null);
      setPassportEditMode({});
    }
  };
  const cancelPassportUpdates = async () => {
    const pending = pendingBulkSaveRef.current;
    if (pending) {
      pendingBulkSaveRef.current = null;
      const currentMetadata = {};
      if (employeeData?.passportNumber) currentMetadata.documentNumber = employeeData.passportNumber;
      if (employeeData?.passportExpiry) currentMetadata.expiryDate = employeeData.passportExpiry;
      if (employeeData?.passportIssue) currentMetadata.issueDate = employeeData.passportIssue;
      await bulkSaveEmployeeDocuments({ ...pending, sharedMetadata: Object.keys(currentMetadata).length ? currentMetadata : undefined });
    }
    setPassportUpdatePromptOpen(false);
    setPassportExtractedNew(null);
    setPassportUpdatePayload(null);
    setPassportEditMode({});
  };

  const applyOhcUpdates = async () => {
    if (!ohcUpdatePayload || !employeeData?.id) { setOhcUpdatePromptOpen(false); return; }
    try {
      const pending = pendingBulkSaveRef.current;
      if (pending) {
        pendingBulkSaveRef.current = null;
        await bulkSaveEmployeeDocuments(pending);
      }
      const payload = buildEmployeeUpdatePayloadFromOhc(ohcUpdatePayload);
      const token = getAuthToken();
      await fetch(`${API_PATHS.V1}/employees/${employeeData.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      setEmployeeData(prev => prev ? { ...prev, ...ohcUpdatePayload } : prev);
    } catch (e) {
      console.error('[TicketDetail] Failed to apply OHC updates:', e);
    } finally {
      setOhcUpdatePromptOpen(false);
      setOhcExtractedNew(null);
      setOhcUpdatePayload(null);
      setOhcEditMode({});
    }
  };
  const cancelOhcUpdates = async () => {
    const pending = pendingBulkSaveRef.current;
    if (pending) {
      pendingBulkSaveRef.current = null;
      const currentMetadata = {};
      if (employeeData?.ohcIssueDate) currentMetadata.issueDate = employeeData.ohcIssueDate;
      if (employeeData?.ohcExpiryDate) currentMetadata.expiryDate = employeeData.ohcExpiryDate;
      await bulkSaveEmployeeDocuments({ ...pending, sharedMetadata: Object.keys(currentMetadata).length ? currentMetadata : undefined });
    }
    setOhcUpdatePromptOpen(false);
    setOhcExtractedNew(null);
    setOhcUpdatePayload(null);
    setOhcEditMode({});
  };

  const applyDrivingLicenseUpdates = async () => {
    if (!drivingLicenseUpdatePayload || !employeeData?.id) { setDrivingLicenseUpdatePromptOpen(false); return; }
    try {
      const pending = pendingBulkSaveRef.current;
      if (pending) {
        pendingBulkSaveRef.current = null;
        await bulkSaveEmployeeDocuments(pending);
      }
      const payload = buildEmployeeUpdatePayloadFromDrivingLicense(drivingLicenseUpdatePayload);
      const token = getAuthToken();
      await fetch(`${API_PATHS.V1}/employees/${employeeData.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      setEmployeeData(prev => prev ? { ...prev, ...drivingLicenseUpdatePayload } : prev);
    } catch (e) {
      console.error('[TicketDetail] Failed to apply Driving License updates:', e);
    } finally {
      setDrivingLicenseUpdatePromptOpen(false);
      setDrivingLicenseExtractedNew(null);
      setDrivingLicenseUpdatePayload(null);
      setDrivingLicenseEditMode({});
    }
  };
  const cancelDrivingLicenseUpdates = async () => {
    const pending = pendingBulkSaveRef.current;
    if (pending) {
      pendingBulkSaveRef.current = null;
      const currentMetadata = {};
      if (employeeData?.drivingLicenseIssueDate) currentMetadata.issueDate = employeeData.drivingLicenseIssueDate;
      if (employeeData?.drivingLicenseExpiryDate) currentMetadata.expiryDate = employeeData.drivingLicenseExpiryDate;
      if (employeeData?.drivingLicensePlaceOfIssue) currentMetadata.placeOfIssue = employeeData.drivingLicensePlaceOfIssue;
      await bulkSaveEmployeeDocuments({ ...pending, sharedMetadata: Object.keys(currentMetadata).length ? currentMetadata : undefined });
    }
    setDrivingLicenseUpdatePromptOpen(false);
    setDrivingLicenseExtractedNew(null);
    setDrivingLicenseUpdatePayload(null);
    setDrivingLicenseEditMode({});
  };

  // Upload additional document with metadata
  const uploadAdditionalDocument = async () => {
    if (!additionalDocFile || !additionalDocMeta.name.trim()) {
      alert('Please select a file and provide a name for the document');
      return;
    }

    const selectedType = additionalDocMeta.type;

    try {
      setUploading(true);

      const token = getAuthToken();

      if (!token) {
        console.error('Authentication token not found');
        setUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', additionalDocFile);
      formData.append('ticketId', ticket?.ticketNumber || ticketId);
      formData.append('name', additionalDocMeta.name);
      const category =
        (additionalDocMeta.type || '').toLowerCase() === 'other'
          ? (otherTypeName || '').trim()
          : String(additionalDocMeta.type || '').trim();

      if (category) {
      formData.append('category', category);
      }
      // Send document type (new) and legacy 'type' for backward compatibility
      if (additionalDocMeta.type) {
        formData.append('documentType', additionalDocMeta.type);
        formData.append('type', additionalDocMeta.type);
      }
      // If 'other' selected, include user-provided label
      if ((additionalDocMeta.type||'').toLowerCase() === 'other' && otherTypeName) {
        formData.append('otherTypeName', otherTypeName);
      }
      
      // Add optional metadata
      if (additionalDocMeta.description) {
        formData.append('description', additionalDocMeta.description);
      }
      if (additionalDocMeta.type) {
        formData.append('type', additionalDocMeta.type);
      }
      

      
      const response = await fetch(
        `${API_PATHS.API}/file/upload/additional-document`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );
      
      if (response.ok) {
        // Parse response to get filePath for employee document flow
        let responseData = null;
        try {
          responseData = await response.json();
        } catch (e) {
          console.warn('[TicketDetail] Could not parse upload response as JSON:', e);
        }

        // If this is a known employee doc type, save to employee documents using filePath from response
        const uploadedFilePath = responseData?.filePath || responseData?.data?.filePath;
        if (employeeUid && isEmployeeDocType(selectedType) && uploadedFilePath) {
          processEmployeeDocumentUpload({
            file: additionalDocFile,
            docTypeCode: selectedType,
            filePath: uploadedFilePath,
            fileName: additionalDocFile.name,
            fileSize: additionalDocFile.size
          }).catch(err => {
            console.error('[TicketDetail] Employee document flow failed (non-blocking):', err);
          });
        }

        // Clear form and refresh documents
        setAdditionalDocFile(null);
        setAdditionalDocMeta({
          name: '',
          description: '',
          type: 'GENERAL'
        });

        // Show success message
        setUploadSuccess(true);
        setUploadMessage('Document uploaded successfully');

        // Mark that document was uploaded but status not changed
        setIsStatusChanged(false);

        // Close modal if using one
        setShowUploadModal(false);

        // Auto-refresh full ticket details so new document appears immediately
        try {
          await fetchTicketDetails(true);
        } catch (e) {
          console.warn('Auto refresh after upload failed, will fallback to partial docs refresh:', e?.message || e);
        }

        // Refresh the document list
        // Use ticket.ticketNumber for the refresh since we're uploading to this ticket
        if (ticket && ticket.ticketNumber) {
          fetchAdditionalDocs(ticket.ticketNumber.toString());
        }
      } else {
        try {
          const errorData = await response.json();
          console.error('Upload error:', errorData);
        } catch (e) {
          console.error('Upload failed with status:', response.status);
        }
      }
    } catch (error) {
      console.error('Error uploading document:', error);
    } finally {
      setUploading(false);
    }
  };

  // Fetch additional documents for the ticket
  const fetchAdditionalDocs = async (ticketIdParam) => {
    console.log(`Starting to fetch additional documents for ticket: ${ticketIdParam}`);
    
    let numericId = null;
    
    // Use the ticketNumber field from the ticket object
    // This is the correct numeric ID field for additional documents
    if (ticket && ticket.ticketNumber) {
      numericId = ticket.ticketNumber;
      console.log(`Using ticket.ticketNumber (${numericId}) for fetching additional documents`);
    } else {
      console.error('Missing ticket.ticketNumber field in ticket object for fetching additional documents');
      console.log('Ticket object for fetch:', ticket);
      return; // Exit early if we can't find the ID
    }
    
    console.log(`Fetching additional documents with numeric ID: ${numericId}`);
    
    try {
      setLoadingAdditionalDocs(true);
      
      const token = getAuthToken();
      if (!token) {
        console.error('Authentication token not found. Please log in again.');
        return;
      }
      
      const response = await fetch(
        `${API_PATHS.FILE}/additional-documents/${numericId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        console.error(`Failed to fetch additional documents: ${response.status}`);
        return;
      }
      
      const documents = await response.json();
      console.log('Fetched additional documents:', documents);
      setAdditionalDocuments(documents || []);
    } catch (error) {
      console.error('Error fetching additional documents:', error);
    } finally {
      setLoadingAdditionalDocs(false);
    }
  };


  
  // Fetch organizations for dropdown
  const fetchOrganizations = async () => {
    try {
      const token = getAuthToken();
      
      if (!token) {
        console.error('Authentication token not found');
        return;
      }
      
      // Determine if user is admin or partner
      const isAdmin = auth?.user?.role?.includes('ADMIN');
      
      // Use the main organizations endpoint for all users
      // The backend should handle filtering based on user role
      const endpoint = `${API_PATHS.API}/organizations`;
      
      console.log(`Fetching organizations using endpoint: ${endpoint}`);
      console.log(`User role: ${auth?.user?.role}, isAdmin: ${isAdmin}`);
      
      const response = await fetch(
        endpoint,
        {
          method: 'GET',
          headers: createAuthHeaders()
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('Organizations fetched:', data ? data.length : 0);
        setOrganizations(data || []);
      } else {
        console.error(`Failed to fetch organizations: ${response.status}`);
        // Fallback to user's organization if available
        if (!isAdmin && auth?.user?.organizationId) {
          console.log('Using fallback to user organization');
          const userOrg = {
            id: auth.user.organizationId,
            name: auth.user.organizationName || 'Your Organization'
          };
          setOrganizations([userOrg]);
        }
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      // Add fallback here as well
      if (auth?.user?.organizationId) {
        const userOrg = {
          id: auth.user.organizationId,
          name: auth.user.organizationName || 'Your Organization'
        };
        setOrganizations([userOrg]);
      }
    }
  };
  
  // Additional documents section rendering - disabled due to non-existing API
  // Additional documents are now shown in the main Documents tab

  // Function to confirm document deletion
  const confirmDeleteDocument = (document) => {
    console.log('Confirming deletion for document:', document);
    setDocumentToDelete(document);
    setShowDeleteConfirm(true);
  };
  
  // Function to cancel document deletion
  const cancelDeleteDocument = () => {
    setDocumentToDelete(null);
    setShowDeleteConfirm(false);
  };
  
  // Function to delete an additional document
  const deleteAdditionalDocument = async () => {
    // Check if we have a valid document to delete
    if (!documentToDelete) {
      console.error('No document to delete');
      return;
    }
    
    // For debugging
    console.log('Deleting document:', documentToDelete);
    
    // Get the actual document ID - either from originalDoc or directly
    const docId = documentToDelete.originalDoc?.id || documentToDelete.id;
    
    if (!docId) {
      console.error('No valid document ID found for deletion');
      return;
    }
    
    setDeleteLoading(true);
    
    try {
      const token = getAuthToken();
      if (!token) {
        console.error('Authentication token not found. Please log in again.');
        return;
      }
      
      console.log(`Sending DELETE request for document ID: ${docId}`);
      
      const response = await fetch(
        `${API_PATHS.API}/file/additional-documents/${docId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const docName = documentToDelete.originalDoc?.name || documentToDelete.title || `Document ${docId}`;
        console.log(`Successfully deleted document: ${docName} (ID: ${docId})`);
        
        // Refresh the additional documents list
        if (ticket && ticket.ticketNumber) {
          fetchAdditionalDocs(ticket.ticketNumber.toString());
        }
        
        // Show success message
        setUploadSuccess(true);
        setUploadMessage(`Document "${docName.replace(/\.[^/.]+$/, '')}" deleted successfully`);
      } else {
        console.error(`Failed to delete document: ${response.status}`);
        setUploadSuccess(false);
        setUploadMessage('Failed to delete document. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      setUploadSuccess(false);
      setUploadMessage('Error deleting document: ' + error.message);
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
      setDocumentToDelete(null);
    }
  };
  
  // Document upload handler function to enable uploading supporting documents
  const handleDocumentUpload = async (event) => {
    try {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const selectedType = additionalDocMeta.type;

      setUploading(true);
      setUploadSuccess(false);
      setUploadMessage('');
      
      const token = getAuthToken();
      if (!token) {
        setUploadMessage('Authentication token not found. Please log in again.');
        setUploading(false);
        return;
      }
      
      // Track successful uploads
      let successCount = 0;
      
      // Important: For supporting docs, we need to use the NUMERIC ticket ID
      // AdditionalDocumentController expects a Long, not a UUID
      let numericId = null;
      
      // Use the ticketNumber field from the ticket object
      // This is the correct numeric ID field as seen in the ticket object
      if (ticket && ticket.ticketNumber) {
        numericId = ticket.ticketNumber;
        console.log(`Using ticket.ticketNumber (${numericId}) for document operations`);
      } else {
        console.error('Missing ticket.ticketNumber field in ticket object');
        console.log('Ticket object:', ticket);
      }
      
      console.log(`Using numeric ticket ID for upload: ${numericId}`);
      
      // Process each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileType', 'SUPPORTING_DOC');
        
        console.log(`Uploading file ${i+1}/${files.length}: ${file.name}`);
        
        // Upload the file directly to the additional document endpoint
        // This is more efficient than using two separate API calls
        const fileFormData = new FormData();
        fileFormData.append('file', file);
        fileFormData.append('ticketId', numericId);
        // Use entered document name if provided, otherwise use file name
        const docName = (additionalDocMeta.name || '').trim() || file.name;
        fileFormData.append('name', docName);
        fileFormData.append('uploadedBy', auth?.user?.name || auth?.user?.email || 'Unknown User');
        
        // Send category based on document type selection
        const category =
          (additionalDocMeta.type || '').toLowerCase() === 'other'
            ? (otherTypeName || '').trim()
            : String(additionalDocMeta.type || '').trim();
        if (category) {
          fileFormData.append('category', category);
        }
        
        // Log what we're uploading
        console.log(`Uploading file ${file.name} for ticket ${numericId}`);
        
        // Use the additional document upload endpoint directly
        const uploadResponse = await fetch(
          `${API_PATHS.API}/file/upload/additional-document`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: fileFormData
          }
        );
        
        if (!uploadResponse.ok) {
          console.error(`Failed to upload file ${file.name}: ${uploadResponse.status}`);
          continue;
        }
        
        try {
          // Parse the JSON response from the upload
          const documentData = await uploadResponse.json();
          console.log('Successfully uploaded additional document:', documentData);

          // Increment the success counter
          successCount++;

          setIsStatusChanged(false);

          // Update the additionalDocuments state to show the new document immediately
          setAdditionalDocuments(prev => [...(prev || []), documentData]);

          // If this is a known employee doc type, save to employee documents using filePath from response
          const uploadedFilePath = documentData?.filePath || documentData?.data?.filePath;
          if (employeeUid && isEmployeeDocType(selectedType) && uploadedFilePath) {
            processEmployeeDocumentUpload({
              file: file,
              docTypeCode: selectedType,
              filePath: uploadedFilePath,
              fileName: file.name,
              fileSize: file.size
            }).catch(err => {
              console.error('[TicketDetail] Employee document flow failed for file', file.name, err);
            });
          }
        } catch (error) {
          // If we can't parse JSON, it might be just a file path response
          const responseText = await uploadResponse.text();
          console.log(`File uploaded successfully: ${file.name}, response: ${responseText}`);
          successCount++;
        }
        
        // This duplicate code block is removed as it's causing a syntax error
        // The filePath was already obtained earlier and we've already processed the file
        // The success counter was already incremented in the successful branch above
        
        // The system will automatically associate the file with the ticket
        // based on the file path and ticket ID when we refresh the ticket data
      }
      
      // Set appropriate message based on results
      if (successCount > 0) {
        setUploadSuccess(true);
        setUploadMessage(`Successfully uploaded ${successCount} ${successCount === 1 ? 'document' : 'documents'}`);
        
        // Reset form fields after successful upload
        setAdditionalDocMeta({
          name: '',
          description: '',
          type: additionalDocMeta.type // Keep the document type selection
        });
        setOtherTypeName('');
        
        // Refresh the document list
        fetchSupportingDocs();
      } else {
        setUploadMessage('Failed to upload documents. Please try again.');
      }
      
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading documents:', error);
      setUploadMessage('An error occurred during upload. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '50vh' }}>
        <CircularProgress size={40} thickness={4} />
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Loading ticket details...
        </Typography>
      </Box>
    );
  }

  // Render error state if no ticket data is available
  if (!ticket && error) {
    return (
      <Box sx={{ p: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error" sx={{ fontSize: '1.25rem' }}>
            Ticket Not Found
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '0.875rem', mt: 1 }}>
            The requested ticket could not be loaded. Please go back and try again.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      {/* Main content container */}
      <Box sx={{ width: '100%', p: 2 }}>
        {/* Top information card */}
        <Card sx={{ mb: 3, borderRadius: '8px', overflow: 'hidden' }}>
          <Box sx={{ 
            bgcolor: '#064d51', 
            color: 'white', 
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              size="small" 
              onClick={handleBack}
              sx={{ color: 'white', mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
              Ticket #{ticket && (ticket.orgTicketNumber || ticket.ticketNumber || ticket.uid || 'Loading...')}
            </Typography>
          </Box>
          <IconButton 
            size="small" 
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{ color: 'white' }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
        
        {/* Persistent Service Info Panel - Always visible regardless of active tab */}
        <Box sx={{ 
          px: 3,
          pt: 3,
          pb: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '1rem' }}>
              {ticket.serviceName || 'Service'}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ 
            color: 'text.secondary',
            fontSize: '0.875rem',
            fontStyle: ticket.description ? 'normal' : 'italic'
          }}>
            {ticket.description || 'No description available'}
          </Typography>
        </Box>
        
        <Box sx={{ p: 3 }}>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 400 }}>
                Ticket Status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Debug log to verify what status is being rendered */}
                {console.log('Rendering status chip with status:', ticket.status)}
                <StatusChip status={ticket.status} />
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => setStatusDialogOpen(true)}
                  sx={{
                    fontSize: '0.75rem',
                    height: '28px',
                    padding: '0 12px',
                    minWidth: 'auto',
                    bgcolor: '#064d51', 
                    '&:hover': { bgcolor: '#053a3d' }
                  }}
                  disabled={!isAssignedToMe}
                >
                  Change Status
                </Button>
              </Box>
            </Box>
            
            <Box>
              <Button 
                variant="contained" 
                color="primary" 
                size="small"
                onClick={() => setAssignDialogOpen(true)}
                sx={{ bgcolor: '#064d51', '&:hover': { bgcolor: '#053a3d' } }}
                disabled={!canAssign}
              >
                Assign Ticket
              </Button>
            </Box>
          </Box>
          
          {/* Customer Information Section */}
          <Box sx={{ mt: 2, mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 400 }}>
              Customer
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1rem', fontWeight: 500 }}>
              {(() => {
                // Try to get customer name from static fields first: nameEn, else firstNameEn + lastNameEn
                const staticFields = ticket?.staticFields || [];
                const nameEnValue = staticFields.find(field => field.code === 'nameEn')?.value;
                const firstNameValue = staticFields.find(field => field.code === 'firstNameEn')?.value;
                const lastNameValue = staticFields.find(field => field.code === 'lastNameEn')?.value;
                const combinedName = [firstNameValue, lastNameValue].filter(Boolean).join(' ').trim();
                return nameEnValue || combinedName || ticket?.customerName || ticket?.customer?.name || 'Unknown Customer';
              })()} 
            </Typography>
          </Box>
          
          {/* Payment Status Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 400 }}>
              Payment Status
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <StatusChip status={ticket?.paymentStatus || 'N/A'} />
            </Box>
          </Box>
          
          <Grid container spacing={3}>

            {/* Regular fields excluding Created Date, Created By, and Item */}
            {ticket.fields && ticket.fields.filter(field => {
              const label = (field.label || field.title || '').toLowerCase();
              return !isDocumentField(field) && 
                     !label.includes('created date') && 
                     !label.includes('created by') && 
                     !label.includes('item');
            }).map((field, index) => (
              <Grid item xs={12} sm={6} md={3} key={`field-${index}`}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 400 }}>
                  {field.label || field.title}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                  {field.value || 'N/A'}
                </Typography>
              </Grid>
            ))}
            
            {/* Static fields excluding Created Date, Created By, and Item */}
            {ticket.staticFields && ticket.staticFields.filter(field => {
              const code = (field.code || field.codeString || '').toLowerCase();
              return !isDocumentField(field) && 
                     !code.includes('created date') && 
                     !code.includes('created by') && 
                     !code.includes('item');
            }).map((field, index) => (
              <Grid item xs={12} sm={6} md={3} key={`static-field-${index}`}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 400 }}>
                  {field.code || field.codeString}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                  {field.value || 'N/A'}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Card>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Main content with tabs */}
      <Card sx={{ borderRadius: '8px', overflow: 'hidden' }}>
        <Box sx={{ 
          bgcolor: 'secondary.main', 
          color: 'common.white', 
          p: 2
        }}>
          <Typography variant="subtitle1" sx={{ fontSize: '1.125rem', fontWeight: 600 }}>
            Details
          </Typography>
        </Box>
        <Divider sx={{ borderColor: 'divider' }} />
        
        <Box sx={{ p: 0 }}>
          {/* Tab navigation using unified TabsNav */}
          {(() => {
            const tabs = [
              { key: 'basic-info', label: 'Basic Info', icon: <DescriptionIcon /> },
              { key: 'actions', label: 'Actions', icon: <TableChartIcon /> },
              { key: 'documents', label: 'Documents', icon: <InsertDriveFileIcon /> },
              { key: 'history', label: 'History', icon: <HistoryIcon /> },
            ];
            if (canSeeAssignment) {
              tabs.push({ key: 'assignment', label: 'Assignment', icon: <AssignmentIcon /> });
            }
            const valueIndex = Math.max(0, tabs.findIndex(t => t.key === activeTab));
            return (
              <TabsNav
                value={valueIndex}
                onChange={(idx) => setActiveTab(tabs[idx]?.key || 'basic-info')}
                items={tabs.map(t => ({ label: t.label, icon: t.icon }))}
              />
            );
          })()}
          

          {/* Tab content */}
          <Box sx={{ p: 3 }}>
            {activeTab === 'basic-info' && (
              <Box>
                <Card className="MuiPaper-root MuiPaper-elevation MuiPaper-rounded MuiPaper-elevation1 MuiCard-root" sx={{ mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                        Basic Information
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={handleOpenTransactionNumberDialog}
                      >
                        Edit
                      </Button>
                    </Box>
                    <Grid container spacing={2}>
                      {/* Created Date */}
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
                          Created Date
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                          {(ticket?.createDateTime || ticket?.createdAt) &&
                            FORMAT_DATE(ticket.createDateTime || ticket.createdAt) || 'N/A'}
                        </Typography>
                      </Grid>

                      {/* Completion Date */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
                          Completion Date
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                          {ticket?.completedAt ? FORMAT_DATE(ticket.completedAt) : 'N/A'}
                        </Typography>
                      </Grid>

                      {/* Created By */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
                          Created By
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                          {ticket?.createdBy || 'N/A'}
                        </Typography>
                      </Grid>
                      
                      {/* Transaction Number */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
                          Transaction Number
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                          {ticket?.transactionNumber || 'N/A'}
                        </Typography>
                      </Grid>
                      
                      {/* Assignment Information */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
                          Assigned To
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                          {ticket.assignment && ticket.assignment.assignedBy === "system" ? ticket.assignment.assignedTo : 
                           (ticket.assignedUser ? ticket.assignedUser.name : 
                            (ticket.assignedOrganisation ? ticket.assignedOrganisation.name : 
                             (ticket.assignment && ticket.assignment.displayName ? ticket.assignment.displayName : 'Not assigned')))}
                        </Typography>
                      </Grid>
                      
                      {/* Claimed By */}
                      {ticket.claimedBy && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="body2" color="textSecondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
                            Claimed By
                          </Typography>
                          <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                            {ticket.claimedBy.name || ticket.claimedBy.emailId || 'Unknown'}
                          </Typography>
                        </Grid>
                      )}
                      
                      {/* Customer Organization */}
                      {ticket.customerOrganisation && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="body2" color="textSecondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
                            Customer Organization
                          </Typography>
                          <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                            {typeof ticket.customerOrganisation === 'string' ? ticket.customerOrganisation : (ticket.customerOrganisation.name || 'Unknown')}
                          </Typography>
                        </Grid>
                      )}
                      
                      {/* Item fields */}
                      {ticket.fields && ticket.fields.filter(field => {
                        const label = (field.label || field.title || '').toLowerCase();
                        return label.includes('item');
                      }).map((field, index) => (
                        <Grid item xs={12} sm={6} key={`item-field-${index}`}>
                          <Typography variant="body2" color="textSecondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
                            {field.label || field.title}
                          </Typography>
                          <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                            {field.value || 'N/A'}
                          </Typography>
                        </Grid>
                      ))}
                      
                      {/* Item static fields */}
                      {ticket.staticFields && ticket.staticFields.filter(field => {
                        const code = (field.code || field.codeString || '').toLowerCase();
                        return code.includes('item');
                      }).map((field, index) => (
                        <Grid item xs={12} sm={6} key={`item-static-field-${index}`}>
                          <Typography variant="body2" color="textSecondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
                            {field.code || field.codeString}
                          </Typography>
                          <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 500 }}>
                            {field.value || 'N/A'}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                    
                    <Typography variant="h6" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '1.25rem', mb: 2, mt: 4, fontWeight: 600 }}>
                      Description
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        {ticket.description ? (
                          <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
                            {ticket.description}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', fontWeight: 400 }}>
                            No description provided for this ticket.
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
            )}
            
            {activeTab === 'actions' && (
              <Box sx={{ mb: 2 }}>
              
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography 
                  variant='subtitle1'
                  sx={{ mb: 2, fontWeight: 600 }}
                >
                  Actions
                </Typography>
                {ticket?.status && !['CLOSED', 'COMPLETED'].includes(ticket.status.toUpperCase()) && (
                <Button 
                variant="outlined" 
                color="primary" 
                size="small"
                onClick={handleOpenAddActionDialog}
                startIcon={<AddIcon />}
                sx={{ mr: 1, color: '#064d51', borderColor: '#064d51', '&:hover': { borderColor: '#053a3d', backgroundColor: '#f0f6f6' } }}
              >
                Add Action
              </Button>
              )}
              </Box>
                {(() => {
                  const actionsList = Array.isArray(ticket?.actions)
                    ? ticket.actions
                    : (Array.isArray(ticket?.ticket?.actions) ? ticket.ticket.actions : []);
                  return actionsList.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><b>Action</b></TableCell>
                          <TableCell><b>Status</b></TableCell>
                          <TableCell><b>Due Date</b></TableCell>
                          <TableCell><b>Remarks</b></TableCell>
                          {ticket?.status && !['CLOSED', 'COMPLETED'].includes(ticket.status.toUpperCase()) && (
                            <TableCell align="center" sx={{ width: 100 }}><b>Edit</b></TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        { actionsList.map((action, index) => {
                          const name = action?.action?.name || action?.name || action?.label || action?.code || action?.action || 'N/A';
                          return (
                            <TableRow key={index}>
                              <TableCell>{name}</TableCell>
                              <TableCell>{action?.status || 'N/A'}</TableCell>
                              <TableCell>{action?.dueDate ? FORMAT_DATE(action.dueDate) : 'N/A'}</TableCell>
                              <TableCell>{action?.remarks || '-'}</TableCell>
                              {ticket?.status && !['CLOSED', 'COMPLETED'].includes(ticket.status.toUpperCase()) && (
                                <TableCell align="center" sx={{ width: 100, py: 0.5 }}>
                                  <IconButton size="small" aria-label="edit action" onClick={() => handleOpenEditActionDialog(action)} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                    <EditIcon fontSize="small" sx={{ color: 'text.primary' }} />
                                  </IconButton>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No actions available for this ticket.
                    </Typography>
                  );
                })()}
              </Box>
            )}
            
            {activeTab === 'documents' && (
              <Box>
                <Divider sx={{ borderColor: 'divider', mb: 2 }} />
                {renderDocumentsSection()}
                
                {/* Document Fields section removed as requested */}
                
                {/* Document upload section */}
                {ticket?.status && !['CLOSED', 'COMPLETED'].includes(ticket.status.toUpperCase()) && (
                  <Box sx={{ mt: 4 }}>
                    <Divider sx={{ borderColor: 'divider', mb: 2 }} />
                    <Typography variant="subtitle1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '1.125rem', mb: 2, fontWeight: 600 }}>
                      Upload New Documents
                    </Typography>
                    {/* Document Type selector */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="doc-type-label" sx={{ fontSize: '0.82rem' }}>Document Type</InputLabel>
                          <Select
                            labelId="doc-type-label"
                            label="Document Type"
                            value={additionalDocMeta.type || ''}
                            onChange={(e) => setAdditionalDocMeta(prev => ({...prev, type: e.target.value }))}
                            sx={{ fontSize: '0.85rem' }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  '& .MuiMenuItem-root': {
                                    fontSize: '0.85rem',
                                    minHeight: '28px',
                                    py: 0.5
                                  }
                                }
                              }
                            }}
                          >
                            {/* Prefer DB-backed types */}
                            {documentTypes.map(dt => (
                              <MenuItem key={dt.code} value={dt.code} sx={{ fontSize: '0.85rem' }}>
                                {dt.label || dt.code}
                              </MenuItem>
                            ))}
                            {/* Always available types */}
                            <MenuItem key="GENERAL" value="GENERAL" sx={{ fontSize: '0.85rem' }}>General</MenuItem>
                            <MenuItem key="other" value="other" sx={{ fontSize: '0.85rem' }}>Other Document</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {(additionalDocMeta.type || '').toLowerCase() === 'other' && (
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Other Type Name"
                            value={otherTypeName}
                            onChange={(e) => setOtherTypeName(e.target.value)}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Document Name"
                          value={additionalDocMeta.name}
                          onChange={(e) => setAdditionalDocMeta(prev => ({...prev, name: e.target.value}))}
                        />
                      </Grid>
                    </Grid>
                    
                    <Box
                      component="label"
                      htmlFor="upload-document"
                      sx={{
                        border: '2px dashed #ccc',
                        borderRadius: '8px',
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#064d51',
                          bgcolor: 'rgba(6, 77, 81, 0.05)'
                        }
                      }}
                    >
                      <input
                        type="file"
                        id="upload-document"
                        style={{ display: 'none' }}
                        ref={fileInputRef}
                        accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.doc,.docx,.xls,.xlsx"
                        multiple
                        onChange={handleDocumentUpload}
                        disabled={uploading}
                      />
                      <FileUploadIcon sx={{ fontSize: 40, color: '#064d51', mb: 1 }} />
                      <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem' }}>
                        Drag & drop files here or click to browse
                      </Typography>
                      <Typography variant="caption" align="center" color="text.secondary" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem' }}>
                        Supported: PDF, JPG, JPEG, PNG, GIF, BMP, DOC, DOCX, XLS, XLSX
                      </Typography>
                    </Box>
                    
                    {uploading && (
                      <Box sx={{ mt: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', ml: 1 }}>
                          Uploading...
                        </Typography>
                      </Box>
                    )}
                    
                    {uploadSuccess && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', color: 'success.main' }}>
                          {uploadMessage}
                        </Typography>
                      </Box>
                    )}
                    
                    {!uploadSuccess && uploadMessage && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", fontSize: '0.875rem', color: 'error.main' }}>
                          {uploadMessage}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
            
            
            {activeTab === 'history' && (
              <Box>
                <Divider sx={{ borderColor: 'divider', mb: 2 }} />
                {renderActivityLogs()}
              </Box>
            )}
            
            {(canSeeAssignment && activeTab === 'assignment') && (
              <Box>
                <AssignmentPanel 
                  ticketId={ticket.id || ticket.ticketId} 
                  ticket={ticket} 
                  onAssignmentComplete={handleRefresh} 
                />
              </Box>
            )}
          </Box>
        </Box>
      </Card>
      
      {/* Display remark if available */}
      {ticket?.remark && (
        <Card sx={{ mt: 3, borderRadius: '8px', overflow: 'hidden' }}>
          <CardContent>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {ticket.remark}
            </Typography>
          </CardContent>
        </Card>
      )}
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={cancelDeleteDocument}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          sx: {
            maxWidth: '400px',
            width: '100%',
            borderRadius: '4px',
            p: 1
          }
        }}
      >
        <DialogTitle id="delete-dialog-title" sx={{ 
          fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", 
          fontSize: '1rem', 
          fontWeight: 600,
          p: 1.5,
          pb: 1
        }}>
          Confirm Document Deletion
        </DialogTitle>
        <DialogContent sx={{ p: 1.5, pt: 0.5 }}>
          <DialogContentText id="delete-dialog-description" sx={{ 
            fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
            fontSize: '0.875rem',
            color: 'text.primary'
          }}>
            Are you sure you want to delete this document?
            {documentToDelete && (
              <strong>
                {" "}{documentToDelete.title?.replace(/\.[^/.]+$/, '') || "Document"}
              </strong>
            )}
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 1, pt: 0.5 }}>
          <Button 
            onClick={cancelDeleteDocument} 
            sx={{ 
              textTransform: 'none',
              fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
              fontSize: '0.8125rem',
            }}
          >
            CANCEL
          </Button>
          <Button 
            onClick={deleteAdditionalDocument} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
            sx={{ 
              textTransform: 'none',
              fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
              fontSize: '0.8125rem',
              bgcolor: '#d32f2f',
              '&:hover': {
                bgcolor: '#b71c1c'
              },
              boxShadow: 'none',
              borderRadius: '4px',
              minWidth: '70px',
              px: 2
            }}
          >
            {deleteLoading ? 'Deleting...' : 'DELETE'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Action Dialog */}
      <Dialog open={addActionDialogOpen} onClose={handleCloseAddActionDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add Action</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="action-select-label">Action</InputLabel>
              <Select
                labelId="action-select-label"
                label="Action"
                value={selectedActionId}
                onChange={handleActionChange}
              >
                {(availableActions || []).map((a) => {
                  const id = a?.id;
                  const name = a?.action?.name || a?.name || a?.label || a?.code || `Action ${id}`;
                  return (
                    <MenuItem key={id} value={id}>{name}</MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <TextField
              label="Due Date"
              type="date"
              value={actionDueDate}
              onChange={(e) => setActionDueDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label="Status"
              value="PENDING"
              size="small"
              InputProps={{ readOnly: true }}
              fullWidth
            />

            <TextField
              label="Remarks (optional)"
              value={actionRemarks}
              onChange={(e) => setActionRemarks(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddActionDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAddAction} disabled={!selectedActionId}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Action Dialog */}
      <Dialog open={editActionDialogOpen} onClose={handleCloseEditActionDialog} fullWidth maxWidth="sm">
        <DialogTitle>Edit Action</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Action display only */}
            <TextField
              label="Action"
              value={editActionData.name || ''}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
            />

            {/* Status dropdown */}
            <FormControl fullWidth size="small">
              <InputLabel id="edit-status-label">Status</InputLabel>
              <Select
                labelId="edit-status-label"
                label="Status"
                value={editActionData.status || 'PENDING'}
                onChange={(e) => setEditActionData(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="PENDING">PENDING</MenuItem>
                <MenuItem value="IN_PROGRESS">IN_PROGRESS</MenuItem>
                <MenuItem value="COMPLETED">COMPLETED</MenuItem>
              </Select>
            </FormControl>

            {/* Due Date */}
            <TextField
              label="Due Date"
              type="date"
              value={editActionData.dueDate || ''}
              onChange={(e) => setEditActionData(prev => ({ ...prev, dueDate: e.target.value }))}
              size="small"
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            {/* Remarks */}
            <TextField
              label="Remarks (optional)"
              value={editActionData.remarks || ''}
              onChange={(e) => setEditActionData(prev => ({ ...prev, remarks: e.target.value }))}
              size="small"
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditActionDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEditAction}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Quick Assign Dialog */}
      {ticket && (
        <QuickAssignDialog
          open={assignDialogOpen}
          onClose={handleCloseAssignDialog}
          ticketId={ticket.id || ticket.ticketId}
          ticket={ticket}
          onAssignmentComplete={() => {
            // Refresh ticket data after assignment
            fetchTicketDetails();
            handleCloseAssignDialog();
          }}
        />
      )}

      {/* Status Change Dialog */}
      {ticket && (
        <StatusChangeDialog
          open={statusDialogOpen}
          onClose={() => setStatusDialogOpen(false)}
          ticket={ticket}
          onStatusChanged={handleStatusChange}
        />
      )}
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={cancelDeleteDocument}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          sx: {
            maxWidth: '400px',
            width: '100%',
            borderRadius: '4px',
            p: 1
          }
        }}
      >
        <DialogTitle id="delete-dialog-title" sx={{ 
          fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", 
          fontSize: '1rem', 
          fontWeight: 600,
          p: 1.5,
          pb: 1
        }}>
          Confirm Document Deletion
        </DialogTitle>
        <DialogContent sx={{ p: 1.5, pt: 0.5 }}>
          <DialogContentText id="delete-dialog-description" sx={{ 
            fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
            fontSize: '0.875rem',
            color: 'text.primary'
          }}>
            Are you sure you want to delete this document?
            {documentToDelete && (
              <strong>
                {" "}{documentToDelete.title?.replace(/\.[^/.]+$/, '') || "Document"}
              </strong>
            )}
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 1, pt: 0.5 }}>
          <Button 
            onClick={cancelDeleteDocument} 
            sx={{ 
              textTransform: 'none',
              fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
              fontSize: '0.8125rem',
            }}
          >
            CANCEL
          </Button>
          <Button 
            onClick={deleteAdditionalDocument} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
            sx={{ 
              textTransform: 'none',
              fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
              fontSize: '0.8125rem',
              bgcolor: '#d32f2f',
              '&:hover': {
                bgcolor: '#b71c1c'
              },
              boxShadow: 'none',
              borderRadius: '4px',
              minWidth: '70px',
              px: 2
            }}
          >
            {deleteLoading ? 'Deleting...' : 'DELETE'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Back Confirmation Dialog */}
      <Dialog
        open={showBackConfirmDialog}
        onClose={handleCancelLeave}
        aria-labelledby="back-confirm-dialog-title"
        aria-describedby="back-confirm-dialog-description"
        PaperProps={{
          sx: {
            maxWidth: '400px',
            width: '100%',
            borderRadius: '4px',
            p: 1
          }
        }}
      >
        <DialogTitle id="back-confirm-dialog-title" sx={{ 
          fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif", 
          fontSize: '1rem', 
          fontWeight: 600,
          p: 1.5,
          pb: 1
        }}>
          Unsaved Changes
        </DialogTitle>
        <DialogContent sx={{ p: 1.5, pt: 0.5 }}>
          <DialogContentText id="back-confirm-dialog-description" sx={{ 
            fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
            fontSize: '0.875rem',
            color: 'text.primary'
          }}>
            You have uploaded documents but haven't changed the ticket status. Do you want to leave?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 1, pt: 0.5 }}>
          <Button 
            onClick={handleCancelLeave} 
            sx={{ 
              textTransform: 'none',
              fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
              fontSize: '0.8125rem',
            }}
          >
            STAY
          </Button>
          <Button 
            onClick={handleConfirmLeave} 
            color="primary" 
            variant="contained"
            sx={{ 
              textTransform: 'none',
              fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
              fontSize: '0.8125rem',
              boxShadow: 'none',
              borderRadius: '4px',
              minWidth: '70px',
              px: 2
            }}
          >
            LEAVE
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Quick Assign Dialog */}
      {ticket && (
        <QuickAssignDialog
          open={assignDialogOpen}
          onClose={handleCloseAssignDialog}
          ticketId={ticket.id || ticket.ticketId}
          ticket={ticket}
          onAssignmentComplete={() => {
            // Refresh ticket data after assignment
            fetchTicketDetails();
            handleCloseAssignDialog();
          }}
        />
      )}
      
      {/* Transaction Number Edit Dialog */}
      <Dialog
        open={transactionNumberDialogOpen}
        onClose={handleCloseTransactionNumberDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Ticket Details</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update the transaction number{isTicketCompleted() ? ' and completion date' : ''} for this ticket.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Transaction Number"
            type="text"
            fullWidth
            variant="outlined"
            value={editingTransactionNumber}
            onChange={(e) => setEditingTransactionNumber(e.target.value)}
            disabled={updatingTransactionNumber}
          />
          {isTicketCompleted() && (
            <TextField
              margin="dense"
              label="Completion Date"
              type="date"
              fullWidth
              variant="outlined"
              value={editingCompletionDate}
              onChange={(e) => setEditingCompletionDate(e.target.value)}
              disabled={updatingTransactionNumber}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseTransactionNumberDialog}
            disabled={updatingTransactionNumber}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateTransactionNumber}
            variant="contained"
            color="primary"
            disabled={updatingTransactionNumber}
            startIcon={updatingTransactionNumber ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {updatingTransactionNumber ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Passport Update Confirmation Dialog */}
      <Dialog
        open={passportUpdatePromptOpen}
        onClose={cancelPassportUpdates}
        fullWidth
        maxWidth="sm"
        aria-labelledby="passport-update-dialog-title"
      >
        <DialogTitle id="passport-update-dialog-title" sx={{ fontSize: '1rem', fontWeight: 600, py: 1.5 }}>Update Passport Info</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2, fontSize: '0.9rem', lineHeight: 1.4 }}>
            We detected new passport information from the uploaded file. Review the changes below and confirm if you want to update the employee profile.
          </DialogContentText>
          {passportExtractedNew?.diffs && passportExtractedNew.diffs.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>Field</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>Current</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>New</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {passportExtractedNew.diffs.map((d, i) => (
                  <TableRow key={`pdiff-${i}`}>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>{d.field}</TableCell>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>{d.current}</TableCell>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {passportEditMode?.[d.key] ? (
                          <>
                            <TextField
                              size="small"
                              type={d.key === 'passportExpiry' ? 'date' : 'text'}
                              value={String(passportUpdatePayload?.[d.key] ?? '') || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                setPassportUpdatePayload(prev => ({ ...(prev || {}), [d.key]: val }));
                              }}
                              sx={{ minWidth: d.key === 'passportExpiry' ? 170 : 220 }}
                              inputProps={{ style: { fontSize: '0.9rem' } }}
                            />
                            <Button
                              onClick={() => setPassportEditMode(prev => ({ ...prev, [d.key]: false }))}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                            >
                              Done
                            </Button>
                          </>
                        ) : (
                          <>
                            <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                              {d.key === 'passportExpiry'
                                ? formatDateForDialog(passportUpdatePayload?.[d.key] || d.next)
                                : (passportUpdatePayload?.[d.key] ?? d.next)}
                            </Typography>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => setPassportEditMode(prev => ({ ...prev, [d.key]: true }))}
                                sx={{ ml: 0.5 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>No changes detected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelPassportUpdates} sx={{ fontSize: '0.85rem' }}>Keep Existing</Button>
          <Button variant="contained" color="primary" onClick={applyPassportUpdates} sx={{ fontSize: '0.85rem' }}>Update Profile</Button>
        </DialogActions>
      </Dialog>

      {/* OHC Update Confirmation Dialog */}
      <Dialog
        open={ohcUpdatePromptOpen}
        onClose={cancelOhcUpdates}
        fullWidth
        maxWidth="sm"
        aria-labelledby="ohc-update-dialog-title"
      >
        <DialogTitle id="ohc-update-dialog-title" sx={{ fontSize: '1rem', fontWeight: 600, py: 1.5 }}>Update OHC Info</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2, fontSize: '0.9rem', lineHeight: 1.4 }}>
            We detected new OHC information from the uploaded file. Review the changes below and confirm if you want to update the employee profile.
          </DialogContentText>
          {ohcExtractedNew?.diffs && ohcExtractedNew.diffs.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>Field</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>Current</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>New</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ohcExtractedNew.diffs.map((d, i) => (
                  <TableRow key={`ohcdiff-${i}`}>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>{d.field}</TableCell>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>{d.current}</TableCell>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {ohcEditMode?.[d.key] ? (
                          <>
                            <TextField
                              size="small"
                              type="date"
                              value={String(ohcUpdatePayload?.[d.key] ?? '') || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                setOhcUpdatePayload(prev => ({ ...(prev || {}), [d.key]: val }));
                              }}
                              sx={{ minWidth: 170 }}
                              inputProps={{ style: { fontSize: '0.9rem' } }}
                            />
                            <Button
                              onClick={() => setOhcEditMode(prev => ({ ...prev, [d.key]: false }))}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                            >
                              Done
                            </Button>
                          </>
                        ) : (
                          <>
                            <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                              {formatDateForDialog(ohcUpdatePayload?.[d.key] || d.next)}
                            </Typography>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => setOhcEditMode(prev => ({ ...prev, [d.key]: true }))}
                                sx={{ ml: 0.5 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>No changes detected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelOhcUpdates} sx={{ fontSize: '0.85rem' }}>Keep Existing</Button>
          <Button variant="contained" color="primary" onClick={applyOhcUpdates} sx={{ fontSize: '0.85rem' }}>Update Profile</Button>
        </DialogActions>
      </Dialog>

      {/* Driving License Update Confirmation Dialog */}
      <Dialog
        open={drivingLicenseUpdatePromptOpen}
        onClose={cancelDrivingLicenseUpdates}
        fullWidth
        maxWidth="sm"
        aria-labelledby="dl-update-dialog-title"
      >
        <DialogTitle id="dl-update-dialog-title" sx={{ fontSize: '1rem', fontWeight: 600, py: 1.5 }}>Update Driving License Info</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2, fontSize: '0.9rem', lineHeight: 1.4 }}>
            We detected new Driving License information from the uploaded file. Review the changes below and confirm if you want to update the employee profile.
          </DialogContentText>
          {drivingLicenseExtractedNew?.diffs && drivingLicenseExtractedNew.diffs.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>Field</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>Current</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>New</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {drivingLicenseExtractedNew.diffs.map((d, i) => (
                  <TableRow key={`dldiff-${i}`}>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>{d.field}</TableCell>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>{d.current}</TableCell>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {drivingLicenseEditMode?.[d.key] ? (
                          <>
                            <TextField
                              size="small"
                              type={d.key === 'drivingLicensePlaceOfIssue' ? 'text' : 'date'}
                              value={String(drivingLicenseUpdatePayload?.[d.key] ?? '') || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                setDrivingLicenseUpdatePayload(prev => ({ ...(prev || {}), [d.key]: val }));
                              }}
                              sx={{ minWidth: d.key === 'drivingLicensePlaceOfIssue' ? 220 : 170 }}
                              inputProps={{ style: { fontSize: '0.9rem' } }}
                            />
                            <Button
                              onClick={() => setDrivingLicenseEditMode(prev => ({ ...prev, [d.key]: false }))}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                            >
                              Done
                            </Button>
                          </>
                        ) : (
                          <>
                            <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                              {d.key === 'drivingLicensePlaceOfIssue'
                                ? (drivingLicenseUpdatePayload?.[d.key] || d.next)
                                : formatDateForDialog(drivingLicenseUpdatePayload?.[d.key] || d.next)}
                            </Typography>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => setDrivingLicenseEditMode(prev => ({ ...prev, [d.key]: true }))}
                                sx={{ ml: 0.5 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>No changes detected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDrivingLicenseUpdates} sx={{ fontSize: '0.85rem' }}>Keep Existing</Button>
          <Button variant="contained" color="primary" onClick={applyDrivingLicenseUpdates} sx={{ fontSize: '0.85rem' }}>Update Profile</Button>
        </DialogActions>
      </Dialog>

      {/* Document Viewer Modal */}
      <DocumentViewer
        open={documentViewerOpen}
        onClose={() => setDocumentViewerOpen(false)}
        documentUrl={currentDocument?.url}
        fileName={currentDocument?.fileName}
        uid={currentDocument?.uid}
        documentType={currentDocument?.documentType}
      />
    </Box>
</>
);
};

export default TicketDetailView;
